/**
 * Chat component module for the main conversational interface.
 * Manages chat state, streaming, user input, and tool call side panel integration.
 *
 * @module components/chat/chat
 */

// External libraries
import type { ReasoningEffort } from "@taqat/core/types";
import { useLlmModels } from "@/hooks/use-llm-models";
import { useChat } from "@ai-sdk/react";
import type { CompactionEvent } from "@taqat/ai";
import { SUPER_AGENT_ID } from "@taqat/core/super-agent";
import { DefaultChatTransport } from "ai";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Bug, FolderClosed, FolderOpen, Link2, MoreHorizontal, Pencil, Share2, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

// Local imports — hooks
import { useSession } from "@/hooks";
import { useAgentData } from "@/hooks/use-agent-data";
import { useAgents } from "@/hooks/use-agents";
import { useAISettings } from "@/hooks/use-ai-settings";
import { useBubbleEmbed } from "@/hooks/use-bubble-embed";
import { useChatEvents } from "@/hooks/use-chat-events";
import { useTeamInfo } from "@/hooks/use-team-info";
import { useToolPanel } from "@/hooks/use-tool-panel";
import { chatService } from "@/services/chat-service";
import { teamChatService } from "@/services/teams-service";

// Local imports — utilities
import { getAuthHeader } from "@/utils/auth-storage";
import { cn } from "../../lib/utils";

// Local imports — UI components
import { AgentIcon } from "@/components/agents/agent-icon";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChatBubbleHeader } from "./chat-bubble-header";
import { ChatCreditsBadge } from "./chat-credits-badge";
import {
  usePublicEmbedConfig,
  applyEmbedPrimaryColor,
} from "@/hooks/use-public-embed-config";
import { useEmbedLocale } from "@/lib/embed-locale";
import { ChatEmptyState } from "./chat-empty-state";
import { ChatTitle, type ChatTitleHandle } from "./chat-title";
import { DeleteChatDialog } from "./delete-chat-dialog";
import { FileBrowser } from "./file-browser";
import { FollowupSuggestions } from "./followup-suggestions";
import { FormQuickAction } from "./form-quick-action";
import { useChatMessageQueue, type ChatSubmitOptions } from "./hooks/use-chat-message-queue";
import { MultimodalInput } from "./input/multimodal-input";
import { useInteractiveChat } from "./interactive";
import { Messages } from "./messages/messages";
import { useToolVisibility } from "@/hooks/use-tool-visibility";
import { ThinkingMessage } from "./messages/thinking-message";
import { ShareDialog } from "./share-dialog";
import { ToolCallSidePanel } from "./tool-views/tool-call-sidepanel";
import { DebugExplorerPanel } from "./debug/debug-explorer-panel";
import { canUseDebugExplorer } from "./debug/use-chat-debug";

// Local imports — types
import { Artifact } from "@/components/artifacts/artifact";
import { useContainerWidth } from "@/hooks/use-container-width";
import { handleArtifactStreamEvent } from "@/lib/artifacts/data-stream-handler";
import { useArtifactStore } from "@/store/artifact-store";
import { useSuperAgentSettingsStore } from "@/store/super-agent-settings-store";
import { useCoworkStore } from "@/store/cowork-store";
import { PLAN_DATA_TYPE, isPlanPayload } from "@/types/plan";
import { usePlanStore } from "@/store/plan-store";
import { PlanChecklistCard } from "@/components/chat/plan/plan-checklist-card";
import { CoworkApprovalCard } from "@/components/cowork/cowork-approval-card";
import { CoworkComposerBar } from "@/components/cowork/cowork-composer-bar";
import { CoworkContextPanel } from "@/components/cowork/cowork-context-panel";
import { CoworkChatSync } from "@/components/cowork/cowork-chat-sync";
import type { Agent } from "@/types/agent-types";
import type { Team } from "@/types/team-types";
import { Attachment, ChatMessage, type ChatSurface } from "../../types/chat-message";
import { AskQuestionCard } from "./ask-question/ask-question-card";
import {
  ShowFormCard,
  type PendingShowForm,
} from "./show-form/show-form-card";
import {
  ComposioConnectCard,
  type ComposioConnectResult,
  type PendingComposioConnect,
} from "./composio-connect/composio-connect-card";
import type {
  AskUserAnswers,
  AskUserNotes,
  AskUserQuestionInput,
  PendingAskQuestion,
} from "@/types/ask-question";

// File-agent integration — local file editing via File System Access API
import { BrowserWorkspaceBanner } from "@/features/file-agent/components/browser-workspace-banner";
import { FileTreePanel } from "@/features/file-agent/components/file-tree-panel";
import { LocalFileViewer } from "@/features/file-agent/components/local-file-viewer";
import { LOCAL_FS_TOOL_NAMES, dispatchLocalFsToolCall, ensureReadWritePermission } from "@/features/file-agent/lib/fs-tools";
import { useFolderAttachment } from "@/features/file-agent/lib/use-folder-attachment";
import { useLocalFileViewerStore } from "@/features/file-agent/store/local-file-viewer-store";
import type { AgentCallStreamStats } from "./messages/agent-call-stream-state";
import { estimateDisplayTokenCount } from "./messages/text-metrics";

/**
 * Width below which the chat condenses its header and pads its composer.
 * Decoupled from any specific host (artifact canvas, sidebar embed, mobile
 * drawer): if the chat ends up narrower than this, it adapts on its own.
 */
const NARROW_CHAT_WIDTH_PX = 600;

/**
 * Stream payload for incremental sub-agent response text.
 * Lets the UI append live content without relying on unsafe casts.
 */
interface AgentCallStreamData {
  /** Agent responsible for the streamed chunk. */
  agentId: string;
  /** Incremental text received for the active agent call. */
  chunk: string;
  /** Optional stream part kind, e.g. reasoning or answer text. */
  kind?: string;
}

/**
 * Stream payload signalling that a sub-agent preview is complete.
 * Allows the UI to clear temporary live content for that agent.
 */
interface AgentCallStreamEndData {
  /** Agent whose temporary stream preview has finished. */
  agentId: string;
}

/**
 * Additional request metadata sent alongside a user message.
 * Keeps the chat request body typed for agent, team, and multi-agent modes.
 */
interface ChatRequestBody {
  /** Full agent configuration when chatting with a specific agent. */
  agent?: unknown;
  /** Supervisor-selected specialists included for multi-agent routing. */
  selectedAgents?: Agent[];
  /** Team identifier when the conversation belongs to a team chat. */
  teamId?: string;
  /** App identifier for scoping SuperAgent handoffs to the current app. */
  appId?: string;
  /** Model override for SuperAgent main chat. */
  modelOverride?: string;
  /** Canonical reasoning rung for this turn; omitted means the model default. */
  reasoningEffort?: ReasoningEffort;
  /** Marks an internal trigger message so the optimistic UI hides it. */
  hidden?: boolean;
  /** Marks a queued item promoted while interrupting the active response. */
  steered?: boolean;
  /**
   * Tells the server this turn was routed through the Super-Agent — used to
   * stamp `chat.metadata.cameFromSuperAgent` so the hand-off chip persists
   * across reloads (#981 / #983).
   */
  fromSuperAgent?: boolean;
}


/**
 * Product surfaces this component renders. Defined in `@/types/chat-message` so
 * the transcript renderers can read it without importing from their own parent;
 * re-exported here because this is where callers expect to find it.
 */
export type { ChatSurface } from "@/types/chat-message";

/**
 * Props for the Chat component
 */
export interface ChatProps {
  /**
   * URL of the chat component
   */
  url?: string;

  /**
   * API Key passed. This will be used to authenticate requests.
   * Should be used only when we embed the iframe or use the react component directly
   */
  apiKey?: string;

  /**
   * Optional className for custom styling of the chat container.
   */
  className?: string;

  /**
   * Unique identifier for the chat session. If not provided, a new UUID will be generated.
   * Used to load existing chat history and maintain chat state.
   */
  id: string;

  /**
   * Array of initial messages to display when the chat loads.
   * Useful for pre-populating chat history or starting with a specific context.
   */
  initialMessages?: ChatMessage[];
  /**
   * Thinking effort chosen on the agent's landing composer, before this chat
   * existed. Carried through the navigation so the first turn honours it.
   */
  initialReasoningEffort?: ReasoningEffort;

  /**
   * Optional initial compaction events loaded from the chat record's
   * metadata. Surface these as in-stream dividers so the timeline is
   * consistent across page reloads. Live events streamed during the current
   * turn (via `data-compaction` parts) are merged on top of this list.
   */
  initialCompactions?: CompactionEvent[];

  /**
   * `true` when this chat is a Super-Agent-routed conversation — either
   * started on the New Task landing or where the user has at any point
   * delegated to a specialist via the slash menu / specialist cards. Loaded
   * from `chat.metadata.cameFromSuperAgent` so the "Talking to: <Agent>"
   * chip survives reload and the user can swap specialists / × back to the
   * supervisor. Direct sub-agent chats stay `false` and behave normally.
   */
  initialCameFromSuperAgent?: boolean;

  /**
   * Controls whether the chat interface is in read-only mode.
   * When true, users cannot send new messages or interact with the chat input.
   */
  isReadonly?: boolean;
  /**
   * Which product surface this chat is rendering.
   *
   * Only "coding" is named alongside the default, because agent and team chats
   * are already identified by `agentId` / `teamId` — adding values for them
   * would give the same fact two sources of truth. Teammate Code has no such
   * marker: it is a Super-Agent-shaped chat (no agent, no team) that is not the
   * Super Agent, which is exactly the case the derivations below get wrong.
   */
  surface?: ChatSurface;

  /**
   * Automatically resumes the chat session if it was previously interrupted.
   * This is useful for maintaining continuity in conversations.
   */
  autoResume?: boolean;

  /**
   * Whether a turn is running on the server right now, for a surface where the
   * turn may have been started by something other than this tab.
   *
   * Distinct from `autoResume`, which the caller decides outright. Pass the live
   * value here — this component adds the guards that make a live value safe:
   * attach only if this tab has sent nothing, and only once per mount.
   *
   * Exists for automated coding runs. A GitHub automation dispatches a turn
   * server-side, so the page has no POST stream of its own to render, and the
   * transcript query is seeded once at mount — leaving the agent's work
   * invisible until the component remounted. The caller cannot make this
   * decision alone: whether the tab owns the turn is knowable only here, from
   * the send path below.
   */
  attachToLiveTurn?: boolean;

  /**
   * Indicates if the chat is being used in an embed context.
   * When true, URL updates will be handled differently.
   */
  isEmbed?: boolean;

  /**
   * Title of the chat session.
   */
  title?: string;

  /**
   * Initial lucide-react icon name for the chat (kebab-case, e.g. "message-circle").
   * Loaded from `chats.metadata.icon`. New chats get an icon streamed via
   * `data-icon` shortly after the first user message; old chats without
   * a stored icon fall back to a default in the renderer.
   */
  icon?: string | null;

  /**
   * Optional sandbox ID for file browser functionality.
   * When provided, enables the file browser button in the chat header.
   */
  sandboxId?: string;

  /**
   * Optional agent ID for the chat session.
   * This can be used to associate the chat with a specific agent.
   * Mutually exclusive with teamId - a chat is either with an agent or a team.
   */
  agentId?: string | null;

  /**
   * Optional team ID for the chat session.
   * This can be used to associate the chat with a specific team.
   * Mutually exclusive with agentId - a chat is either with an agent or a team.
   */
  teamId?: string | null;

  /**
   * Optional team info for channel details (when already available).
   */
  teamInfo?: Team | null;

  /**
   * Selected agents for the chat session.
   */
  selectedAgents?: string[];

  /**
   * Callback when a new chat is created
   */
  onNewChat?: (chatId: string) => void;

  /**
   * Callback when navigation should occur (for external routing)
   */
  onFinish?: (path: string) => void;

  /**
   * Extra controls for the header's icon row, rendered before the built-in ones.
   *
   * A slot rather than a flag per surface: the Coding surface hangs its diff
   * toggle here, and this component stays unaware that diffs exist — the same
   * reason `CodeLayout` owns the diff column instead of the chat. Hidden along
   * with the rest of the secondary controls when the chat is in a narrow column.
   */
  headerActions?: ReactNode;

  /**
   * Strip rendered beneath the composer, tucked under it as a tray.
   *
   * The composer counterpart of `headerActions`, and a slot for the same
   * reason: the Coding surface hangs its session bar (branch, diff stat,
   * sandbox state) here, built from state the coding page already owns, and
   * this component stays unaware that branches or sandboxes exist. Passed to
   * BOTH composer instances — the empty state's and the conversation's — so a
   * session shows the same strip before and after its first message.
   *
   * A function form is offered for strips whose controls depend on whether a
   * turn is in flight — `streaming` covers submitted and streaming alike. Only
   * this component knows that; a host polling its own session state learns it
   * seconds late, which is long enough to offer a control that is already
   * invalid.
   */
  composerFooter?: ReactNode | ((context: { streaming: boolean }) => ReactNode);

  /**
   * Indicates if the chat is being used in a compact context (like a widget).
   * When true, adjusts layout constraints for narrow containers.
   */
  isCompact?: boolean;

  /**
   * When provided, automatically sends this message on mount.
   * Used by InteractiveChat to kick off the assessment after form submission.
   */
  initialMessage?: string;

  /**
   * Attachments uploaded by the user on the upstream landing page (e.g.
   * the agent detail composer) before the chat existed. They were bound
   * to this chat's id at upload time; we surface them in the composer +
   * include them in the auto-sent first message so the agent receives
   * the file alongside the prompt.
   */
  initialAttachments?: Attachment[];

  /**
   * Indicates if the chat is being used as a preview/test mode (e.g., in agent editor).
   * When true, hides delete button and more options dropdown in the header.
   */
  isPreview?: boolean;

  /**
   * Hides the agent chip (icon + name) in the chat header.
   *
   * Set by the agent chat shell, which already renders the agent's identity
   * above the tabs — without this the name would appear twice. Team, CoWork,
   * and widget chats have no shell above them, so they keep the chip.
   */
  hideAgentChip?: boolean;

  /**
   * Callback fired after a title rename has been persisted successfully.
   * Lets parent screens keep their own title-dependent state in sync.
   */
  onTitleUpdated?: (title: string) => void;

  /**
   * Forwarded to the underlying MultimodalInput. When true and `onLive`
   * is set, the send button morphs to a Live (voice) button while the
   * composer is empty. Owned by the parent page so the live overlay
   * itself can live at page scope and persist across re-renders here.
   */
  voiceLiveEnabled?: boolean;
  onLive?: () => void;
}

/**
 * Get the chat url. Based on the server url /local the chat url will be different.
 * If the server url is /local then the chat url will be /api/chat otherwise
 * it will be {serverUrl}/api/chat
 */
function getChatUrl() {
  const serverUrl = import.meta.env.VITE_API_BASE_URL || "";
  // Remove /api suffix if present, then add /chat
  const cleanUrl = serverUrl.replace(/\/api$/, "");
  return `${cleanUrl}/api/chat`;
}

/** Checks whether an error from useChat is a credit-related error (handled via toast). */
function isCreditError(error: Error): boolean {
  try {
    const parsed = JSON.parse(error.message);
    const t = parsed?.error?.type;
    return t === "INSUFFICIENT_CREDITS" || t === "USER_CAP_EXCEEDED";
  } catch {
    return false;
  }
}

/** Checks whether an error represents an intentional client-side stream stop. */
function isAbortError(error: Error): boolean {
  return error.name === "AbortError" || /abort|cancel/i.test(error.message);
}

/**
 * Write a query param into the address bar without routing.
 *
 * `<Chat>` deliberately outlives its route: once the first stream finishes the
 * page rewrites the URL with `history.replaceState` (see `onNewChat` below and
 * `pages/app/chat/index.tsx`) so the component stays mounted and no skeleton
 * flashes. React Router never observes that write, so its own location stays
 * on the pre-stream path — and `setSearchParams` would navigate relative to
 * that stale location, yanking the URL back to `/chat`, changing `location.key`
 * and remounting `<Chat>` with a fresh id, which wipes the live conversation
 * (#1542). Going straight to `history` keeps the real URL and React Router's
 * state object intact, so nothing remounts.
 *
 * Use this for any in-place URL param update on this page; reach for React
 * Router only when actually navigating somewhere else.
 */
function setUrlParam(key: string, value: string | null): void {
  const url = new URL(window.location.href);
  if (value === null) url.searchParams.delete(key);
  else url.searchParams.set(key, value);
  window.history.replaceState(window.history.state, "", url);
}

/**
 * Chat component for displaying and interacting with a chat session.
 * This component manages the chat state, handles user input, and displays chat messages.
 * It also handles real-time updates and integrates with the chat API.
 */
export function Chat({
  url = getChatUrl(),
  id,
  className = "",
  isReadonly = false,
  surface = "assistant",
  initialMessages = [] as ChatMessage[],
  initialCompactions = [] as CompactionEvent[],
  initialCameFromSuperAgent = false,
  autoResume = false,
  attachToLiveTurn = false,
  // isEmbed = false,
  title = "New Chat",
  icon = null,
  sandboxId,
  agentId = null,
  initialReasoningEffort,
  teamId = null,
  teamInfo: providedTeamInfo = null,
  selectedAgents: _selectedAgents = [],
  onNewChat,
  onFinish,
  headerActions,
  composerFooter,
  isCompact = false,
  initialMessage,
  initialAttachments,
  isPreview = false,
  hideAgentChip = false,
  onTitleUpdated,
  voiceLiveEnabled = false,
  onLive,
}: ChatProps) {
  /**
   * Teammate Code.
   *
   * Several derivations below infer "this is the Super Agent" from the absence
   * of an agent and a team. That inference is right for every assistant surface
   * and wrong for coding, which has neither but is not the Super Agent — so it
   * was offered specialists to delegate to, blocked by the model-selection
   * guard, and given Super-Agent follow-up chips. This flag is what those
   * derivations exclude.
   */
  const isCodingSurface = surface === "coding";

  /** Whether the right-side tool call panel is enabled (VITE_ENABLE_TOOL_SIDEPANEL) */
  const isToolSidepanelEnabled = import.meta.env.VITE_ENABLE_TOOL_SIDEPANEL === "true";

  /**
   * Workspace tool-visibility flags (issue #1332). The side panel exclusively
   * renders tool output, so it is only available when output is visible (which
   * is already false when tool calls are hidden) and once the settings have
   * resolved — otherwise it would surface tool calls/output the workspace hides.
   */
  const toolVisibility = useToolVisibility();
  const canShowToolSidepanel =
    isToolSidepanelEnabled && toolVisibility.resolved && toolVisibility.showToolOutput;

  /**
   * List of attachments which is added to the multi model inputs.
   * We dont keep the attachments when load the chat.
   * it will be available in the messages
   *
   * Seeded from `initialAttachments` so files attached on the upstream
   * landing page (e.g. agent detail) survive the navigation handoff and
   * the auto-sent first message includes them.
   */
  const [attachments, setAttachments] = useState<Attachment[]>(
    initialAttachments ?? [],
  );
  /**
   * React Router navigation function.
   * Used for programmatic routing after chat creation, deletion, or agent edits.
   */
  const navigate = useNavigate();
  const { appId: routeAppId } = useParams<{ appId?: string }>();

  /**
   * Chat Debug Explorer — a dev/admin-only side panel showing the ground-truth
   * timeline (traces, tokens, cost, tool I/O, compactions) for this chat.
   * Opens via three gated triggers: the header bug icon, `⌘⇧D`, or `?debug=1`.
   */
  const debugAllowed = canUseDebugExplorer();
  const [searchParams] = useSearchParams();
  const [isDebugOpen, setIsDebugOpen] = useState(
    () => debugAllowed && searchParams.get("debug") === "1",
  );

  /** Toggle the debug panel and mirror its state into the `?debug=1` URL param (shareable). */
  const toggleDebug = useCallback(
    (next?: boolean) => {
      if (!debugAllowed) return;
      setIsDebugOpen((prev) => {
        const open = next ?? !prev;
        setUrlParam("debug", open ? "1" : null);
        return open;
      });
    },
    [debugAllowed],
  );

  /** Keyboard shortcut: ⌘⇧D / Ctrl+Shift+D toggles the debug panel. */
  useEffect(() => {
    if (!debugAllowed) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        toggleDebug();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [debugAllowed, toggleDebug]);

  /**
   * Bubble embed mode detection and parent-frame communication.
   * Controls whether URL updates are suppressed and provides a close callback for the embed shell.
   */
  const { isBubbleMode, notifyParentClose } = useBubbleEmbed({ chatId: id, agentId });

  /**
   * Embedded-widget surface (iframe or bubble, `embed=true` in the URL).
   * The main app never carries this param, so embed appearance config
   * (spec 075 — Phase 2) can never leak into the normal chat UI.
   */
  const isEmbedSurface = useMemo(
    () => new URLSearchParams(window.location.search).get("embed") === "true",
    []
  );

  /**
   * Public embed appearance config (headerText/primaryColor/toggles) —
   * fetched only on the embed surface; null elsewhere or when unset.
   */
  const { config: embedAppearance } = usePublicEmbedConfig(agentId, isEmbedSurface);

  /**
   * Embed session locale (spec 075 — Phase 3): sent with every chat turn so
   * the agent soft-defaults replies to the visitor's chosen language. Kept in
   * a ref because the transport closure is captured on first render (same
   * pattern as agentRoutingRef).
   */
  const embedSessionLocale = useEmbedLocale();
  const embedLocaleRef = useRef<string | null>(null);
  embedLocaleRef.current = isEmbedSurface ? embedSessionLocale : null;

  /**
   * Theme the embed iframe with the configured primary color. Runs inside the
   * iframe's own document (embed surface only), overriding `--primary` so the
   * send/submit controls match the host brand instead of Taqat orange.
   */
  useEffect(() => {
    if (isEmbedSurface) applyEmbedPrimaryColor(embedAppearance?.primaryColor);
  }, [isEmbedSurface, embedAppearance?.primaryColor]);

  /**
   * Agent details and multi-agent selection state.
   * Fetches agent metadata from the API and restores any previously selected agents.
   */
  const { agentData, agents, setAgents } = useAgentData(agentId, _selectedAgents);

  /**
   * Composer attachments: the agent-level setting, further restricted by the
   * embed config's `enableFileUpload` on the embed surface.
   */
  const attachmentsAllowed =
    (agentData?.agentSettings?.enableAttachments ?? true) &&
    (!isEmbedSurface || (embedAppearance?.enableFileUpload ?? true));
  /**
   * Active sub-agent inside a Super-Agent chat — set when the user picks a
   * specialist via the slash menu or the empty-state cards. When non-null it
   * becomes the effective agent for stream requests, persistence, and routing,
   * so a hand-off in the UI actually re-keys the chat to that sub-agent
   * (fixes #981 + #983).
   */
  const [activeAgent, setActiveAgent] = useState<Agent | null>(null);
  /**
   * Pending `ask_user_question` tool call awaiting a user submission. The
   * agent is suspended on the AI SDK's tool-call wait; we hold the call
   * here and render `<AskQuestionCard>` in place of the composer until the
   * user submits (resolves to `{ answers, notes? }`) or dismisses
   * (resolves to `{ declined: true }`).
   */
  /**
   * Workspace has a Super-Agent model configured in DB
   * (`workspaces.settings.chat.modelId`). Used purely to satisfy the
   * client-side "model required" guard without sending the UUID as
   * `modelOverride` — the server resolves the workspace model itself via
   * `loadWorkspaceConfig`.
   */
  const [hasWorkspaceModel, setHasWorkspaceModel] = useState(false);
  const [pendingAskQuestion, setPendingAskQuestion] =
    useState<PendingAskQuestion | null>(null);
  /**
   * Pending `show_form` tool call (spec 075 — Phase 5). Held open while the
   * ShowFormCard renders the named View in place of the composer; submit
   * resolves the tool with the STRUCTURED values object, dismiss with
   * `{ status: 'dismissed' }`. Precondition (`requires` prefill) and unknown
   * form ids are resolved immediately with typed results the model
   * self-corrects from (see the resolution effect below).
   */
  const [pendingShowForm, setPendingShowForm] =
    useState<PendingShowForm | null>(null);

  /**
   * Named-View definitions for show_form — same public embed config cache;
   * fetched on demand the moment a show_form call arrives (also outside the
   * embed surface, e.g. builder preview of a published agent).
   */
  const { config: viewsConfig, resolved: viewsResolved } = usePublicEmbedConfig(
    agentId,
    Boolean(pendingShowForm)
  );
  /**
   * Pending `connect_composio_integration` tool call. Set when the agent asks
   * the user to connect an integration; the ComposioConnectCard replaces the
   * composer until the user connects (resolves `{ connected, profileId }`) or
   * dismisses (resolves `{ declined: true }`).
   */
  const [pendingComposioConnect, setPendingComposioConnect] =
    useState<PendingComposioConnect | null>(null);
  /**
   * Tool-call IDs that have already triggered `sendAutomaticallyWhen`.
   * After the first fire the server's reply keeps the resolved part in the
   * assistant message; without dedupe, every subsequent stream-end
   * re-evaluates the check, finds the same resolved part, and fires
   * another POST — a loop that only breaks when the agent finally emits
   * a fresh input-available tool call. Keyed by `toolCallId` so each
   * pending card's resolution triggers exactly one auto-send.
   */
  const autoSentToolCallIdsRef = useRef<Set<string>>(new Set());
  /**
   * Local mirror of the persisted `cameFromSuperAgent` flag. Starts from the
   * server-loaded chat record and flips to `true` the moment the user picks
   * a specialist this session (so the chip appears immediately, before the
   * next stream stamps the flag on the row). Cleared by the × handler.
   */
  const [cameFromSuperAgent, setCameFromSuperAgent] = useState<boolean>(
    initialCameFromSuperAgent
  );
  /**
   * `true` immediately after the user × clears the hand-off. The URL is
   * updated silently via `history.replaceState` to avoid the parent page's
   * skeleton-flash on a full RR navigation, so React Router still sees the
   * old `agentId` param — we use this flag to override every derived value
   * (effectiveAgent, isSuperAgentRoute, chipAgent) so the chat instantly
   * behaves as if it's routed to the supervisor.
   */
  const [clearedToSuperAgent, setClearedToSuperAgent] = useState(false);
  // The clearing API call is fast and the endpoint is idempotent, so we
  // don't need a dedicated "clearing" UI state for now — the existing
  // `isLoading`-based disabled flag on the chip is enough to prevent
  // double-clicks during an in-flight request.

  // `clearedToSuperAgent` overrides everything — once × is hit, the chat
  // routes through the supervisor again until the user picks another
  // specialist (which flips the flag back off via handleActiveAgentChange).
  const effectiveAgent = clearedToSuperAgent ? null : (activeAgent ?? agentData);
  const effectiveAgentId = clearedToSuperAgent
    ? SUPER_AGENT_ID
    : (activeAgent?.id ?? agentId);
  /**
   * Super-Agent route: the URL is the supervisor's (or there's no agent at
   * all — the workspace landing). The slash menu's "Agent" entry and the
   * specialist hand-off chip only make sense here; a direct sub-agent chat
   * has no router to delegate from.
   */
  const isSuperAgentRoute =
    !isCodingSurface &&
    (clearedToSuperAgent || !agentId || agentId === SUPER_AGENT_ID);
  /**
   * The conversation is Super-Agent-routable when either the URL is the
   * supervisor's OR the chat row carries the cameFromSuperAgent flag (the
   * user previously delegated to a specialist from the Super-Agent). The
   * slash agent picker is gated on this — direct sub-agent chats hide it.
   */
  const isSuperAgentRouted = isSuperAgentRoute || cameFromSuperAgent;
  /**
   * Latest agent routing, kept in a ref so SDK-initiated turns route to the
   * CURRENT agent. `useChat` captures the transport (and its
   * `prepareSendMessagesRequest` closure) on first render, when `agentData` is
   * still loading and `effectiveAgent` is null. `handleSendMessage` dodges this
   * by passing routing in the per-call body, but the SDK's auto-continuation
   * after an `ask_user_question` answer (via `sendAutomaticallyWhen`) carries NO
   * per-call body — so reading `effectiveAgent` directly there sees the stale
   * first-render null, the body ships without `agent`, and the server falls back
   * to the Super-Agent, bypassing the direct agent's workspace capability gating
   * (#1243). Refreshing this ref every render keeps the closure reading current.
   */
  const agentRoutingRef = useRef<Record<string, unknown>>({});
  agentRoutingRef.current = effectiveAgent
    ? { agent: effectiveAgent }
    : agents.length > 0
      ? { selectedAgents: agents }
      : {};
  if (isSuperAgentRouted) agentRoutingRef.current.fromSuperAgent = true;
  if (teamId) agentRoutingRef.current.teamId = teamId;

  /**
   * CoWork (desktop, Model C): when a local folder is open, every turn carries
   * the connected device id so the server routes this turn's workspace to the
   * user's machine (a DeviceWorkspace). Kept in a ref because the transport
   * closure is captured on first render (same reason as `agentRoutingRef`).
   */
  const coworkDeviceId = useCoworkStore((s) => (s.mode === "cowork" ? s.deviceId : null));
  const coworkDeviceIdRef = useRef<string | null>(null);
  coworkDeviceIdRef.current = coworkDeviceId;
  /**
   * Which agent the chip names. Three sources, in priority order:
   *   1. activeAgent — the user just picked a specialist this session;
   *   2. URL agentData — for chats persisted as super-agent-routed, the
   *      URL agent IS the active specialist (so the chip survives reload);
   *   3. nothing — direct sub-agent chats, or a super-agent-routed chat
   *      that's currently back at the supervisor.
   */
  // `agentData` is an AgentRecord; the chip only reads `name`/`avatar`, which
  // both shapes share. Cast keeps the chip prop typed as `Agent | null` for
  // the consumer without forcing a wider type on the entire component.
  const chipAgent: Agent | null = activeAgent
    ?? (cameFromSuperAgent && !isSuperAgentRoute && agentData
      ? (agentData as unknown as Agent)
      : null);
  const { state: specialistState, loading: specialistLoading } = useAgents({
    // Coding has no specialists to hand off to, so don't fetch them — an empty
    // list is also what keeps the empty state from rendering the picker.
    autoFetch: !agentId && !teamId && !isCodingSurface,
    initialParams: {
      appId: routeAppId,
      published: true,
      limit: 12,
    },
  });

  const specialistAgents = useMemo(
    () => specialistState.agents.slice(0, 4),
    [specialistState.agents]
  );
  const specialistsLoading = specialistLoading.loading && specialistAgents.length === 0;

  // No auto-selection: when no specialist is picked, the empty selection
  // routes silently to SuperAgent on the backend. Picking a card overrides
  // that and routes to the chosen specialist.

  /** Whether the agent has workspace enabled — drives file explorer visibility.
   *  SuperAgent (no agentId) always has workspace/file tools available. */
  const isWorkspaceEnabled = useMemo(() => {
    if (!agentId) return true;
    const wsConfig = (agentData?.agentSettings as Record<string, unknown> | undefined)?.workspaceConfig as { enabled?: boolean } | undefined;
    return wsConfig?.enabled === true;
  }, [agentId, agentData]);

  /**
   * Whether the agent uses the browser workspace (File System Access API).
   * When true, the local-folder UI is shown and the server-side workspace
   * file browser is hidden — they're mutually exclusive sources of truth.
   */
  const isBrowserWorkspace = useMemo(() => {
    if (!agentId) return false;
    const wsConfig = (agentData?.agentSettings as Record<string, unknown> | undefined)?.workspaceConfig as { enabled?: boolean; environment?: string } | undefined;
    return wsConfig?.enabled === true && wsConfig.environment === "browser";
  }, [agentId, agentData]);

  /**
   * Controls visibility of share dialog
   */
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isFileBrowserOpen, setIsFileBrowserOpen] = useState(false);
  /** Model override for SuperAgent main chat — persisted in app settings */
  const [selectedModel, setSelectedModel] = useState<string>("");
  /**
   * How hard the model should think on the next turn. Undefined means the
   * model's own default, which is a real setting rather than an absence — see
   * the composer control.
   */
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort | undefined>(
    initialReasoningEffort,
  );
  const isMainAssistantChat = !agentId && !teamId && !isCodingSurface;

  /*
    Which rungs the composer may offer.

    Resolved from whichever model this turn will actually use, in the same
    precedence the server applies: an explicit per-turn override, then the
    agent's own configured model, then the workspace default. When none of the
    three resolves, the control renders nothing — fail-closed rather than
    guessing a ladder for an unknown model.
  */
  const { findModel, findModelByRowId, loading: modelsLoading } = useLlmModels({ autoFetch: true });
  const superAgentSettings = useSuperAgentSettingsStore((s) => s.settings);
  const superAgentStatus = useSuperAgentSettingsStore((s) => s.status);
  const loadSuperAgentSettings = useSuperAgentSettingsStore((s) => s.load);
  /**
   * Row id of the workspace Super-Agent model. Settings store the model as a
   * `workspace_custom_models` UUID, not a model string — it is deliberately
   * never sent as `modelOverride` (that once let a stale app-level model bury
   * the workspace one), so it is resolved to capabilities here instead.
   */
  const [workspaceModelRowId, setWorkspaceModelRowId] = useState<string | undefined>(undefined);
  /*
     The SERVED model, not the draft. Chat runs the agent's published snapshot,
     so a model changed but not yet published must not change what this control
     offers — otherwise the rungs on screen describe a model that is not the one
     answering. Falls back to the live config for agents never published.
  */
  const agentModelId = (agentData?.servedModelConfig ?? agentData?.modelConfig)
    ?.model;
  const effectiveModel = selectedModel
    ? findModel(selectedModel)
    : agentModelId
      ? findModel(agentModelId)
      : workspaceModelRowId
        ? findModelByRowId(workspaceModelRowId)
        : undefined;
  const reasoningOptions = effectiveModel?.reasoningOptions;
  /*
    True while the model behind this chat is still being resolved — the model
    list is loading, or (for the workspace assistant) its configured model has
    not arrived yet. The control holds its space with a skeleton rather than
    rendering rungs for a model that may not be the one that answers.
  */
  const reasoningLoading =
    modelsLoading.loading ||
    (isMainAssistantChat &&
      (superAgentStatus === "idle" || superAgentStatus === "loading"));
  /*
    A level restored from the chat is only honoured while the answering model
    still offers it. The model behind a chat can change after the level was
    stored — an agent's model is edited, or the workspace default moves — and
    ladders differ, so a stored `xhigh` can reopen under a model that stops at
    `high`. The server drops rungs it doesn't recognise, so keeping it here
    would only show a control that silently does nothing.

    Applied via effect rather than at useState time because the model list
    loads asynchronously: at first render `reasoningOptions` is undefined,
    which means "not known yet", not "unsupported".
  */
  useEffect(() => {
    if (!reasoningEffort || !reasoningOptions) return;
    if (!reasoningOptions.includes(reasoningEffort)) {
      setReasoningEffort(undefined);
    }
  }, [reasoningEffort, reasoningOptions]);
  /**
   * A model with no gradations is drawn as a switch rather than as a slice of
   * the effort ladder — see the composer control.
   */
  const reasoningMode = effectiveModel?.capabilities?.reasoning?.mode;
  /**
   * True for the workspace Super-Agent chat. Covers both a brand-new chat
   * (no agentId) and a persisted one — once saved, the URL carries the
   * synthetic SUPER_AGENT_ID as the agentId. Used to scope follow-up chips.
   */
  const isSuperAgentChat =
    !isCodingSurface && !teamId && (!agentId || agentId === SUPER_AGENT_ID);

  /**
   * Resolve the Super-Agent chat model source so the submit-time guard
   * knows whether *some* model is configured.
   *
   * The workspace `settings.chat.modelId` (a `workspace_custom_models` row
   * UUID set via Settings → Super Agent) is the single source of truth. We
   * CANNOT send it as `modelOverride` (the server expects a model string,
   * not a UUID), so we only flip `hasWorkspaceModel` to satisfy the guard
   * and let `loadWorkspaceConfig` resolve it server-side.
   *
   * We deliberately no longer read the per-app `settings.chat.model`: apps
   * (`solution_package_installations`) are deprecated, and seeding it into
   * `selectedModel` sent it as a `modelOverride` that buried the workspace
   * Super-Agent model — e.g. a stale app-level `gemma` (8k ctx) overriding
   * a workspace `qwen-3.6` (120k ctx) and forcing premature compaction.
   */
  useEffect(() => {
    if (!isMainAssistantChat || !routeAppId) return;
    void loadSuperAgentSettings();
  }, [isMainAssistantChat, routeAppId, loadSuperAgentSettings]);

  /*
    Subscribed rather than fetched-on-mount. Saving a new Super-Agent model
    from the settings dialog while this chat is open publishes into the store,
    so the thinking control re-resolves against the new model immediately —
    before, it kept describing the previous model until something remounted
    this component, which navigating away happened to do and staying here did
    not.
  */
  useEffect(() => {
    if (!isMainAssistantChat) return;
    if (superAgentSettings?.modelId) {
      setHasWorkspaceModel(true);
      setWorkspaceModelRowId(superAgentSettings.modelId);
    } else {
      // A workspace that has no model configured must not keep a stale one:
      // clearing the model in settings has to clear the control too.
      setHasWorkspaceModel(false);
      setWorkspaceModelRowId(undefined);
    }
  }, [isMainAssistantChat, superAgentSettings]);

  /** Save model to app settings when user changes it */
  const handleModelChange = useCallback((model: string) => {
    setSelectedModel(model);
    /*
      Drop the effort on a model switch rather than carrying it over. Ladders
      differ per model — `max` exists on GPT-5.6 and not on Gemini, `medium`
      does not exist on DeepSeek — so a carried-over rung would be silently
      projected onto something the user never chose.
    */
    setReasoningEffort(undefined);
    if (routeAppId && model) {
      import("@/services/app-service").then(({ updateAppChatModel }) => {
        updateAppChatModel(routeAppId, model).catch(() => { /* save failed silently */ });
      });
    }
  }, [routeAppId]);

  /**
   * Title shown in the header.
   * Kept locally so inline renames and streamed title updates render immediately.
   */
  const [currentTitle, setCurrentTitle] = useState(title);

  /**
   * Icon shown next to the header title.
   * Kept locally so streamed `data-icon` updates render immediately without
   * waiting for a chat reload. Null until the AI emits an icon (or the parent
   * supplies one from `chats.metadata.icon`).
   */
  const [currentIcon, setCurrentIcon] = useState<string | null>(icon);

  /**
   * Tracks whether a manual title rename request is still being saved.
   * Used to disable the inline editor while the API call completes.
   */
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  /**
   * Current user session information
   */
  const session = useSession();

  /**
   * Chat events for real-time updates
   */
  const chatEvents = useChatEvents();

  /**
   * Whether the artifact canvas is open. Drives ONLY the AnimatePresence
   * gate for the canvas itself — the chat panel does not branch on this.
   * Layout decisions (hide secondary controls, pad composer) come from
   * the chat's own measured width via `useContainerWidth` below, so any
   * future host that puts the chat in a narrow column gets the same
   * treatment without having to tell the chat anything.
   */
  const isArtifactVisible = useArtifactStore((s) => s.artifact.isVisible);
  const resetArtifact = useArtifactStore((s) => s.resetArtifact);

  /**
   * Close any artifact canvas left open by a previously-viewed chat. The
   * artifact store is a global singleton overlay, so without this it stays
   * visible — showing the prior chat's artifact — when the user switches chats
   * from the sidebar history. Keyed on the chat `id` so it fires on every chat
   * switch (whether the Chat remounts or just receives a new id prop) but never
   * while an artifact is streaming into the current chat (id unchanged).
   */
  useEffect(() => {
    resetArtifact();
  }, [id, resetArtifact]);

  /**
   * Whether the local-file viewer is open (user double-clicked a file in the
   * tree). Mutually exclusive with the artifact canvas at render time —
   * the artifact takes priority when both flags are true.
   */
  const isLocalFileVisible = useLocalFileViewerStore((s) => s.visible);

  /**
   * Self-measured width of the chat container. The chat reacts to its own
   * size — there is no `isArtifactVisible` / `isSidebarMode` plumbing —
   * which keeps this component reusable across any host that might shrink
   * its column.
   */
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const chatContainerWidth = useContainerWidth(chatContainerRef);
  const isNarrow = chatContainerWidth > 0 && chatContainerWidth < NARROW_CHAT_WIDTH_PX;
  /**
   * Phone-sized viewport (matchMedia, ≤767px). Browser-agnostic — works the
   * same in mobile Chrome / Safari / Edge / Firefox / Opera / Brave. Drives
   * the keyboard-safe empty-state layout (composer anchored above the soft
   * keyboard instead of centered, which iOS scrolls to the top) and hides the
   * AI disclaimer once a conversation is underway on phones.
   */
  const isMobile = useIsMobile();

  /** Admin-only actions are now visible to every authenticated user. */
  const showAdminActions = true;

  /**
   * Get AI settings for supervisor capabilities.
   * Used to control visibility of Computer Preview side panel.
   */
  const { settings: aiSettings } = useAISettings();

  /**
   * Track if this is a new chat (no messages sent yet)
   */
  const [isNewChat, setIsNewChat] = useState(initialMessages.length === 0);

  /**
   * Tracks whether a pre-flight credit error occurred during the current request.
   * Used to suppress onFinish navigation and event emission when the chat was never created.
   */
  const hadCreditErrorRef = useRef(false);

  /**
   * Team metadata for the interactive welcome screen.
   * Fetched from the API (with auth → public fallback) or derived from the provided Team prop.
   */
  const teamInfo = useTeamInfo(teamId, providedTeamInfo);


  /**
   * Chat input state. The chat message will be stored here.
   * This will be used to send the message to the server.
   */
  const [input, setInput] = useState<string>("");

  /**
   * TODO: Quick action input state.
   * Remove this when the quick actions are implemented.
   */
  const [quickActionInput, setQuickActionInput] = useState<string>("");

  /**
   * Super-Agent follow-up chip state.
   * - `chipFilledPromptRef` holds the prompt a chip last dropped into the
   *   composer, so the next send can be recognised as chip-originated.
   * - `lastTurnFromChip` suppresses chips on the turn right after a chip send
   *   (the anti chip-spiral rule).
   */
  const chipFilledPromptRef = useRef<string | null>(null);
  const [lastTurnFromChip, setLastTurnFromChip] = useState<boolean>(false);

  /**
   * Sends a chip's prompt immediately, skipping the composer. We still stamp
   * `chipFilledPromptRef` so the in-flight `submitChat` recognises this turn
   * as chip-originated and the anti chip-spiral rule applies on the next turn.
   */
  const sendChipPrompt = (fullPrompt: string) => {
    chipFilledPromptRef.current = fullPrompt;
    void submitChat(fullPrompt);
  };

  /**
   * Prepares the authentication token. This will be either taking
   * the Bearer token from localStorage (which may have been exchanged from API key)
   * or the user session token or the channel user token.
   */
  const prepareAuthToken = (): Record<string, string> => {
    // Use getAuthHeader which checks for admin token first, then channel user token
    const authHeader = getAuthHeader();
    return {
      Authorization: authHeader || "",
      "X-User-Id": session?.user?.id || "",
      Accept: "application/json",
    };
  };

  /**
   * Track whether the current request is a regeneration.
   * Used to pass isRegenerating flag and messageId to server to delete the specific message.
   */
  const isRegeneratingRef = useRef(false);

  /**
   * Track the message ID that is being regenerated.
   * When regenerating a message, this stores the assistant message ID that should be deleted.
   */
  const regeneratingMessageIdRef = useRef<string | null>(null);

  /**
   * Set when the server streams `data-guardrail-block`, meaning an output
   * guardrail rejected the response we have been rendering.
   *
   * Held in a ref and applied in `onFinish` rather than acted on immediately in
   * `onData`: the block arrives while useChat still owns the in-flight assistant
   * message, and rewriting it mid-stream races the hook's own internal updates
   * (ours would be silently overwritten). Waiting for the stream to settle makes
   * the replacement deterministic and keeps normal streaming untouched.
   */
  const guardrailBlockRef = useRef<{ notice: string; guardrailId: string } | null>(null);

  /**
   * Current status of the streaming chat request.
   * Any data-status will be stored in the status variable.
   */
  const [dataStatus, setDataStatus] = useState<string>("");
  const [hasActiveChatRequest, setHasActiveChatRequest] = useState(false);
  const hasActiveChatRequestRef = useRef(false);

  /**
   * Whether this tab has ever sent a message, and whether it has already
   * attached to a turn it did not start. Both back `attachToLiveTurn`.
   *
   * Refs rather than state because neither should cause a render of its own:
   * they are read while computing `resume` below, and the prop change that
   * matters (`attachToLiveTurn` flipping) re-renders anyway.
   */
  const hasSentFromThisTabRef = useRef(false);
  const attachedToLiveTurnRef = useRef(false);

  /**
   * Whether to ask the SDK for the reconnect stream.
   *
   * `autoResume` is the caller's own decision and passes through untouched.
   * `attachToLiveTurn` is the guarded form, for a page that may be watching a
   * turn nobody in this tab started — an automated coding run, say.
   *
   * The two guards are what keep it from replaying a turn twice. A tab that has
   * sent anything drives its turns through its own POST stream, and resuming
   * alongside that is precisely the duplicate-chunk bug the coding page's old
   * latch was written to avoid. And attaching is one-shot per mount, so the
   * flag settling back to false and true again cannot start a second replay.
   */
  const shouldResume =
    autoResume ||
    (attachToLiveTurn === true &&
      !hasSentFromThisTabRef.current &&
      !attachedToLiveTurnRef.current);

  /*
    Closes the one-shot window. Runs after the render that turned `shouldResume`
    on, and therefore after the SDK has already read it — the effect that calls
    `resumeStream` is registered inside `useChat` below, and effects fire in
    registration order, so this cannot cancel the attach it is recording.
  */
  useEffect(() => {
    if (attachToLiveTurn === true && !hasSentFromThisTabRef.current) {
      attachedToLiveTurnRef.current = true;
    }
  }, [attachToLiveTurn]);

  /**
   * Compaction events combined from the persisted chat metadata
   * (`initialCompactions`) and live `data-compaction` stream parts received
   * during this turn. Rendered in-line by Messages as horizontal dividers
   * between the messages they were anchored to.
   */
  const [compactions, setCompactions] = useState<CompactionEvent[]>(initialCompactions);

  /** True while a manual `/compact` request is in flight (guards double-fire). */
  const [isCompacting, setIsCompacting] = useState(false);

  /**
   * Handles the `/compact` slash command: asks the server to summarize older
   * turns, then merges the resulting compaction event into local state so the
   * divider renders immediately (the event is also persisted server-side, so
   * it survives reload). A no-op result means the chat was already compact.
   *
   * In-progress feedback is the composer's "Compacting…" overlay, so no
   * loading toast — only a result toast once the request settles.
   */
  const handleCompact = useCallback(async () => {
    if (isCompacting) return;
    setIsCompacting(true);
    try {
      const response = await chatService.compactChat(id);
      const event = response?.data?.compaction;
      if (event) {
        setCompactions((prev) =>
          prev.some((e) => e.id === event.id) ? prev : [...prev, event],
        );
        toast.success("Conversation compacted");
      } else {
        toast.info("Conversation is already compact");
      }
    } catch {
      toast.error("Couldn't compact the conversation. Please try again.");
    } finally {
      setIsCompacting(false);
    }
  }, [id, isCompacting]);

  /**
   * Accumulated streaming text for in-progress agent calls.
   * Keyed by agentId so each agent card can show its live response.
   * Cleared when the chat finishes streaming.
   */
  const [agentStreams, setAgentStreams] = useState<Record<string, string>>({});
  const agentStreamsRef = useRef<Record<string, string>>({});

  /** Live display metrics for streamed sub-agent calls. */
  const [agentStreamStats, setAgentStreamStats] = useState<Record<string, AgentCallStreamStats>>({});

  /**
   * Final streamed text per agent, preserved after stream-end so cards keep
   * showing the full reply instead of falling back to the master-facing
   * (truncated) tool output. Session-only; reload falls back to `output`.
   */
  const [agentFinalTexts, setAgentFinalTexts] = useState<Record<string, string>>({});

  /**
   * Ref to the ChatTitle component for triggering typing animation.
   * Called when the server streams a `data-title` event so the header updates live.
   */
  const chatTitleRef = useRef<ChatTitleHandle>(null);

  /** Keep local title state aligned when the parent loads a different chat title. */
  useEffect(() => {
    setCurrentTitle(title);
  }, [title]);

  /** Keep local icon state aligned when the parent loads a different chat icon. */
  useEffect(() => {
    setCurrentIcon(icon);
  }, [icon]);

  /**
   * Local folder attachment for the file-editing agent.
   * The handle is persisted per chat in IndexedDB; revisits surface a
   * "Reconnect" button instead of forcing the user to re-pick the folder.
   */
  // The chat is locked to a single folder once attached. We let the chat
  // inherit a folder picked on the agent landing page by passing agentId
  // as a fallback key — the hook copies it forward and then locks it.
  const folderAttachment = useFolderAttachment(id, {
    fallbackKey: agentId ?? undefined,
  });

  /**
   * Vercel AI chat hook.
   * This will be used to send the message to the server and get the response.
   * It will also handle the chat messages and the chat state.
   */
  const {
    messages,
    status,
    error,
    stop,
    setMessages,
    sendMessage,
    addToolResult,
    regenerate: originalRegenerate,
  } = useChat<ChatMessage>({
    id,
    messages: initialMessages,
    experimental_throttle: 100,
    resume: shouldResume,
    // Continue the chat after a client-side interactive tool result lands
    // post-reload — the original SSE stream is gone, so without an explicit
    // trigger the agent never sees the answer. Scoped to the two interactive
    // tools (ask_user_question, connect_composio_integration); the generic
    // lastAssistantMessageIsCompleteWithToolCalls was intentionally disabled
    // in 7ab297e9 to keep executeStream's loop semantics intact. Fixes #1019
    // resume-after-navigate path.
    sendAutomaticallyWhen: ({ messages: msgs }) => {
      const last = msgs[msgs.length - 1];
      if (!last || last.role !== "assistant") return false;
      const CLIENT_TOOL_TYPES = new Set([
        "tool-ask_user_question",
        "tool-connect_composio_integration",
        "tool-show_form",
      ]);
      // Scan the whole message — NOT just the last step. The merger
      // collapses retry-duplicates into a leader that may live in an
      // earlier step with trailing reasoning/step-start/text from the
      // retry-step's leftovers, so a last-step-only scan misses it.
      // Dedupe by toolCallId via `autoSentToolCallIdsRef`: AI SDK 6
      // re-invokes sendAutomaticallyWhen on every stream end, and the
      // resolved part stays in the message; without the ref we'd POST
      // once for the user's submit, then again for each subsequent
      // stream end until the agent finally emits a fresh
      // input-available tool call. Recording the ID we just fired for
      // collapses that into exactly one auto-send per resolution.
      const resolvedIds: string[] = [];
      for (const part of last.parts ?? []) {
        const type = (part as { type?: string }).type ?? "";
        if (!CLIENT_TOOL_TYPES.has(type)) continue;
        const state = (part as { state?: string }).state;
        if (state !== "output-available" && state !== "output-error") return false;
        const id = (part as { toolCallId?: string }).toolCallId;
        if (id) resolvedIds.push(id);
      }
      if (resolvedIds.length === 0) return false;
      const seen = autoSentToolCallIdsRef.current;
      const freshIds = resolvedIds.filter((id) => !seen.has(id));
      if (freshIds.length === 0) return false;
      for (const id of freshIds) seen.add(id);
      return true;
    },
    generateId: () => {
      // If regenerating, use the existing message ID to update in place
      // Otherwise generate a new UUID for new messages
      return regeneratingMessageIdRef.current || uuidv4();
    },
    transport: new DefaultChatTransport({
      api: url,
      headers: prepareAuthToken(),
      /**
       * Wrap fetch so we can detect when the server transparently routed
       * this turn to a fallback model (because the user-selected model
       * couldn't ingest the attached file). The server signals this via
       * the `x-model-fallback` response header — we surface it as a toast
       * so the user understands which model actually answered without
       * having to dig through chat metadata.
       *
       * Cast to `typeof fetch` because the SDK's transport types require
       * the static `preconnect` method that wrapper functions don't carry;
       * we forward to the global `fetch` so any preconnect call still works.
       */
      fetch: ((async (input: RequestInfo | URL, init?: RequestInit) => {
        const response = await fetch(input, init);
        const fallback = response.headers.get("x-model-fallback");
        if (fallback) {
          const from = response.headers.get("x-model-fallback-from");
          toast.info(
            from
              ? `Switched to ${fallback} for this message — ${from} can't read attachments.`
              : `Switched to ${fallback} for this message.`,
          );
        }
        return response;
      }) as unknown) as typeof fetch,
      prepareSendMessagesRequest({ messages, id, body }) {
        // Get the isRegenerating flag and message ID
        // Note: Don't reset regeneratingMessageIdRef here as it's used by generateId after this
        const isRegenerating = isRegeneratingRef.current;
        const regeneratingMessageId = regeneratingMessageIdRef.current;

        // Only reset isRegenerating flag here (messageId reset happens in onFinish)
        isRegeneratingRef.current = false;

        // Default agent routing, mirroring handleSendMessage. The SDK initiates
        // some turns itself with NO per-call body — most importantly the
        // auto-continuation after an ask_user_question answer is resolved via
        // addToolResult. Without these defaults that turn arrives with no agent
        // routing at all, so the server falls back to the Super-Agent and
        // bypasses the direct agent's workspace capability gating (#1243). Read
        // from the ref (not the closed-over `effectiveAgent`) because this
        // closure is captured on first render — see `agentRoutingRef`. The
        // per-call `...body` overrides when present.
        const agentRouting = agentRoutingRef.current;

        // Remember CoWork tasks (for the sidebar history) + the folder they run
        // in (so reopening the chat can auto-reconnect that folder).
        if (coworkDeviceIdRef.current && id) {
          const cw = useCoworkStore.getState();
          cw.markCoworkChat(id);
          cw.rememberChatFolder(id, cw.folderPath ?? "");
        }

        return {
          body: {
            id,
            message: messages.at(-1),
            isRegenerating,
            regeneratingMessageId,
            ...(coworkDeviceIdRef.current ? { deviceId: coworkDeviceIdRef.current } : {}),
            ...(embedLocaleRef.current ? { sessionLocale: embedLocaleRef.current } : {}),
            ...agentRouting,
            ...body,
          },
        };
      },
    }),
    onFinish: () => {
      hasActiveChatRequestRef.current = false;
      setHasActiveChatRequest(false);
      setDataStatus("");

      // Reset the regenerating message ID ref after streaming completes
      regeneratingMessageIdRef.current = null;

      /**
       * An output guardrail rejected this response. The server already replaced
       * the offending body in the stored record; mirror that transformation on
       * the copy the user is looking at so the blocked text goes away now rather
       * than on their next reload.
       *
       * Deliberately identical to the server's `finalizeWithBlock`: drop text +
       * reasoning, keep everything else (tool calls stay, so the turn's activity
       * is still visible), append the notice, and set `metadata.error` — which is
       * what makes the renderer show the notice instead of the parts. Matching it
       * exactly is what makes the live view and a reload agree.
       */
      const block = guardrailBlockRef.current;
      if (block) {
        guardrailBlockRef.current = null;
        setMessages((prev) => {
          const lastAssistantIdx = prev.findLastIndex((m) => m.role === "assistant");
          if (lastAssistantIdx === -1) return prev;
          const target = prev[lastAssistantIdx];
          const keptParts = (target.parts ?? []).filter(
            (p) => p.type !== "text" && p.type !== "reasoning",
          );
          const next = [...prev];
          next[lastAssistantIdx] = {
            ...target,
            parts: [...keptParts, { type: "text", text: block.notice }],
            metadata: {
              ...(target.metadata ?? {}),
              error: true,
              errorMessage: block.notice,
            },
          } as ChatMessage;
          return next;
        });
      }

      /**
       * If a pre-flight credit error occurred, the chat was never created on
       * the server.  Skip event emission and navigation to avoid "Chat not
       * found" 404 errors from ChatDetailPage / sidebar fetches.
       */
      if (hadCreditErrorRef.current) {
        hadCreditErrorRef.current = false;
        return;
      }

      /**
       * No step-list settling here either. Whether a trailing `in_progress`
       * step should be completed depends on WHY the turn ended — finished, or
       * paused waiting on the user, or aborted — and only the server sees the
       * finish reason. It settles and re-streams the list itself.
       */

      // Sync agent sidebar without showing its loading indicator. Use the
      // effective agent so a sub-agent that just took over a super-agent chat
      // refreshes under its own history, not the supervisor's.
      chatEvents.emit("update-chat", id, { agentId: effectiveAgentId, teamId });

      // Handle custom finish callback
      if (onFinish) {
        onFinish(id);
      }
    },
    onError: (error) => {
      /** Reset thinking indicator on any error */
      setDataStatus("");
      hasActiveChatRequestRef.current = false;
      setHasActiveChatRequest(false);
      if (isAbortError(error)) {
        return;
      }

      /** Try to parse structured error from the server response body */
      let parsed: { success: boolean; error?: { type?: string; message?: string } } | null = null;
      try {
        parsed = JSON.parse(error.message);
      } catch {
        // Not JSON — fall through to generic toast
      }

      const errorType = parsed?.error?.type;
      const errorMessage = parsed?.error?.message || error.message;
      const isCreditError = errorType === 'INSUFFICIENT_CREDITS' || errorType === 'USER_CAP_EXCEEDED';

      /**
       * For credit errors, remove the optimistic user message that useChat appended
       * before the server rejected the request. This returns the chat to its
       * previous state so the user doesn't see a dangling message with no response.
       * Also flag the ref so onFinish skips navigation/events.
       */
      if (isCreditError) {
        hadCreditErrorRef.current = true;
        setMessages(initialMessages || []);
      }

      if (errorType === 'INSUFFICIENT_CREDITS') {
        toast.info('Credits Exhausted', {
          description: errorMessage || 'Your workspace has run out of credits.',
          action: {
            label: 'Buy Credits',
            onClick: () => navigate(`/app/${routeAppId}/settings/credits`),
          },
        });
        return;
      }

      if (errorType === 'USER_CAP_EXCEEDED') {
        toast.info('Usage Limit Reached', {
          description: errorMessage || 'You have reached your monthly usage limit.',
          action: {
            label: 'View Settings',
            onClick: () => navigate(`/app/${routeAppId}/settings/credits`),
          },
        });
        return;
      }

      toast(errorMessage || 'An error occurred during chat processing', {
        description: errorMessage || 'An error occurred during chat processing',
      });
    },
    /**
     * Client-executed tool dispatch.
     * Local-fs tools are declared on the server but have no `execute`, so
     * the AI SDK forwards the call here. We run the operation against the
     * user-granted FileSystemDirectoryHandle and submit the result back
     * via `addToolResult`. Anything else falls through to the SDK's
     * default handling (server-executed tools).
     */
    onToolCall: async ({ toolCall }) => {
      /**
       * `ask_user_question` is a Codex-style clarification tool. We don't
       * call `addToolResult` here — instead we hold the call open and
       * surface the AskQuestionCard in place of the composer. The user's
       * submission / dismissal resolves the tool via `addToolResult`
       * later (see handleAskQuestionSubmit / handleAskQuestionDismiss).
       */
      if (toolCall.toolName === "ask_user_question") {
        setPendingAskQuestion({
          toolCallId: toolCall.toolCallId,
          input: toolCall.input as AskUserQuestionInput,
        });
        return;
      }

      /**
       * `show_form` renders a named View (pre-authored form). Hold the call
       * open; the resolution effect below validates the form id +
       * preconditions once the view definitions are loaded, then the
       * ShowFormCard collects the structured submission.
       */
      if (toolCall.toolName === "show_form") {
        setPendingShowForm({
          toolCallId: toolCall.toolCallId,
          input: (toolCall.input ?? {}) as PendingShowForm["input"],
        });
        return;
      }

      /**
       * `connect_composio_integration` is a client-side tool too: hold the
       * call open and render the ComposioConnectCard. The user's connect /
       * dismiss resolves it via `addToolResult` (see handleComposioConnected /
       * handleComposioConnectDismiss).
       */
      if (toolCall.toolName === "connect_composio_integration") {
        setPendingComposioConnect({
          toolCallId: toolCall.toolCallId,
          input: toolCall.input as PendingComposioConnect["input"],
        });
        return;
      }

      if (!LOCAL_FS_TOOL_NAMES.has(toolCall.toolName)) return;

      const { handle } = folderAttachment;
      if (!handle) {
        addToolResult({
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          output: {
            success: false,
            message: "No folder is attached. The user must attach a local folder before this tool can run.",
          },
        });
        return;
      }

      const granted = await ensureReadWritePermission(handle);
      if (!granted) {
        addToolResult({
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          output: {
            success: false,
            message: "The user has not granted read/write permission to the folder. Ask them to click 'Reconnect'.",
          },
        });
        return;
      }

      const result = await dispatchLocalFsToolCall(
        toolCall.toolName,
        (toolCall.input as Record<string, unknown>) ?? {},
        { root: handle, chatId: id, toolCallId: toolCall.toolCallId },
      );
      addToolResult({
        tool: toolCall.toolName,
        toolCallId: toolCall.toolCallId,
        output: result,
      });
    },
    onData: (part) => {
      // Artifact stream events (init/delta/finish) drive the canvas overlay
      // and inline preview cards. Forward to the artifact store and short-
      // circuit so the rest of the onData branches don't see them.
      if (handleArtifactStreamEvent(part)) return;
      // Live step list — emitted by the `update_plan` tool, which every non-task
      // agent now binds. The store renders it in the CoWork Context panel and in
      // the chat-universal checklist card, and owns the per-chat cache so the
      // list survives navigating away and back (data parts aren't reliably
      // re-hydrated from history). Distinct from the plan-approval flow below.
      if (part?.type === PLAN_DATA_TYPE) {
        if (isPlanPayload(part.data)) {
          // Empty step list = trivial request → setPlan renders nothing.
          usePlanStore.getState().setPlan(part.data);
        }
        return;
      }
      if (part?.type === 'data-status') {
        setDataStatus(part?.data as string || 'TeamMate is working on your request')
      }
      if (part?.type === 'data-guardrail-block') {
        // Only record it here — the rewrite happens in onFinish. See
        // guardrailBlockRef's declaration for why.
        guardrailBlockRef.current = part.data as { notice: string; guardrailId: string };
      }
      if (part?.type === 'data-compaction') {
        const event = part.data as CompactionEvent;
        setCompactions((prev) => {
          // Skip if we already have this event (e.g., persisted then re-streamed)
          if (prev.some((e) => e.id === event.id)) return prev;
          return [...prev, event];
        });
      }
      if (part?.type === 'data-title') {
        const newTitle = part?.data as string;
        setCurrentTitle(newTitle);
        chatTitleRef.current?.animateTitle(newTitle);
        // Emit event to update sidebar with typing animation
        chatEvents.emit('update-title', id, { title: newTitle, agentId, teamId });
      }
      if (part?.type === 'data-icon') {
        const newIcon = part?.data as string;
        setCurrentIcon(newIcon);
        chatTitleRef.current?.animateIcon?.(newIcon);
        // Emit so the sidebar can fade-swap the row icon
        chatEvents.emit('update-icon', id, { icon: newIcon, agentId, teamId });
      }
      // Accumulate streaming text from sub-agent calls for live preview
      if (part?.type === 'data-agent-call-stream') {
        const { agentId: streamAgentId, chunk } = part.data as AgentCallStreamData;
        const receivedAt = Date.now();
        const nextText = (agentStreamsRef.current[streamAgentId] || '') + chunk;
        agentStreamsRef.current = {
          ...agentStreamsRef.current,
          [streamAgentId]: nextText,
        };
        setAgentStreams(agentStreamsRef.current);
        setAgentStreamStats(prev => {
          const existing = prev[streamAgentId];
          return {
            ...prev,
            [streamAgentId]: {
              startedAt: existing?.startedAt ?? receivedAt,
              updatedAt: receivedAt,
              chunkCount: (existing?.chunkCount ?? 0) + 1,
              estimatedTokens: estimateDisplayTokenCount(nextText),
            },
          };
        });
      }
      if (part?.type === 'data-agent-call-stream-end') {
        const { agentId: streamAgentId } = part.data as AgentCallStreamEndData;
        const endedAt = Date.now();
        const finalText = agentStreamsRef.current[streamAgentId];
        if (finalText) {
          setAgentFinalTexts(prev => ({ ...prev, [streamAgentId]: finalText }));
        }
        const nextStreams = { ...agentStreamsRef.current };
        delete nextStreams[streamAgentId];
        agentStreamsRef.current = nextStreams;
        setAgentStreams(nextStreams);
        setAgentStreamStats(prev => {
          const existing = prev[streamAgentId];
          if (!existing) return prev;
          return {
            ...prev,
            [streamAgentId]: {
              ...existing,
              updatedAt: endedAt,
              endedAt,
            },
          };
        });
      }
    }
  });

  /**
   * Clear lingering agent streams when streaming ends.
   * Promote any unflushed text to finalTexts first so the card still has
   * something to show if the stream-end signal was lost.
   */
  /**
   * Hydrate the step list from the server on chat open.
   *
   * `chats.metadata.plan` is the single source of truth — written by
   * `update_plan` as the agent works and settled by the runner at turn end — so
   * the list comes back identically on a reload, in another tab, or on a second
   * device. Earlier cuts reconstructed it from message parts or localStorage;
   * both were guesses about state the server already knew.
   *
   * Clears first so a slow fetch never shows the previous chat's plan.
   */
  useEffect(() => {
    let cancelled = false;
    usePlanStore.getState().setPlan(null);
    if (!id) return;
    void chatService
      .getChatById(id)
      .then((res) => {
        if (cancelled) return;
        const plan = (res?.data as { metadata?: { plan?: unknown } } | undefined)?.metadata?.plan;
        // A live update that landed while this was in flight wins — it is newer.
        if (usePlanStore.getState().plan) return;
        if (isPlanPayload(plan)) usePlanStore.getState().setPlan(plan);
      })
      .catch(() => {
        /* the panel is not worth failing a chat open over */
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (status === 'ready' && Object.keys(agentStreams).length > 0) {
      const endedAt = Date.now();
      setAgentFinalTexts(prev => ({ ...agentStreams, ...prev }));
      setAgentStreamStats(prev => {
        const next = { ...prev };
        for (const streamAgentId of Object.keys(agentStreams)) {
          const existing = next[streamAgentId];
          if (existing && !existing.endedAt) {
            next[streamAgentId] = {
              ...existing,
              updatedAt: endedAt,
              endedAt,
            };
          }
        }
        return next;
      });
      agentStreamsRef.current = {};
      setAgentStreams({});
    }
  }, [status, agentStreams]);

  /**
   * Rehydrate the pending client-side tool card (ask_user_question /
   * connect_composio_integration) when the chat loads with an unresolved
   * tool call still on the latest assistant message. `useChat`'s
   * `onToolCall` callback only fires for live streaming, so without this
   * the card never reappears after a navigate-away → return and the chat
   * is stuck waiting on an `addToolResult` that can never be submitted.
   * Fixes #1019.
   */
  useEffect(() => {
    if (pendingAskQuestion || pendingComposioConnect || pendingShowForm) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    for (const part of last.parts ?? []) {
      const type = (part as { type?: string }).type;
      if (!type?.startsWith("tool-")) continue;
      const state = (part as { state?: string }).state;
      if (state !== "input-available") continue;
      const toolCallId = (part as { toolCallId?: string }).toolCallId;
      const input = (part as { input?: unknown }).input;
      if (!toolCallId || !input) continue;
      if (type === "tool-ask_user_question") {
        setPendingAskQuestion({
          toolCallId,
          input: input as AskUserQuestionInput,
        });
        return;
      }
      if (type === "tool-connect_composio_integration") {
        setPendingComposioConnect({
          toolCallId,
          input: input as PendingComposioConnect["input"],
        });
        return;
      }
      if (type === "tool-show_form") {
        setPendingShowForm({
          toolCallId,
          input: input as PendingShowForm["input"],
        });
        return;
      }
    }
  }, [messages, pendingAskQuestion, pendingComposioConnect, pendingShowForm]);

  /**
   * show_form resolution (spec 075 — Phase 5). Once the view definitions are
   * loaded: unknown form ids and unmet preconditions resolve the held tool
   * call immediately with TYPED results the model self-corrects from — the
   * user never sees a broken or premature form. A valid call falls through
   * and the ShowFormCard renders.
   */
  const activeShowFormView = useMemo(() => {
    if (!pendingShowForm) return null;
    return (
      (viewsConfig?.views ?? []).find(
        (v) => v.id === pendingShowForm.input.formId
      ) ?? null
    );
  }, [pendingShowForm, viewsConfig]);

  useEffect(() => {
    if (!pendingShowForm || !viewsResolved) return;
    const views = viewsConfig?.views ?? [];
    const view = views.find((v) => v.id === pendingShowForm.input.formId);
    if (!view) {
      addToolResult({
        tool: "show_form",
        toolCallId: pendingShowForm.toolCallId,
        output: {
          status: "error",
          message: `Unknown formId '${pendingShowForm.input.formId}'. Available forms: ${views.map((v) => v.id).join(", ") || "(none configured for this agent)"}.`,
        },
      });
      setPendingShowForm(null);
      return;
    }
    const prefill = pendingShowForm.input.prefill ?? {};
    const missing = (view.requires ?? []).filter(
      (k) => !prefill[k] || String(prefill[k]).trim().length < 3
    );
    if (missing.length > 0) {
      addToolResult({
        tool: "show_form",
        toolCallId: pendingShowForm.toolCallId,
        output: {
          status: "blocked",
          reason: "precondition",
          missing,
          instruction: `Before this form can be shown you must collect from the user: ${missing.join(", ")}. Ask for these in conversation, then call show_form again with them in prefill.`,
        },
      });
      setPendingShowForm(null);
    }
  }, [pendingShowForm, viewsResolved, viewsConfig, addToolResult]);

  /** Resolves show_form with the user's structured submission. */
  const handleShowFormSubmit = useCallback(
    (values: Record<string, unknown>) => {
      if (!pendingShowForm) return;
      addToolResult({
        tool: "show_form",
        toolCallId: pendingShowForm.toolCallId,
        output: {
          status: "submitted",
          formId: pendingShowForm.input.formId,
          values,
        },
      });
      setPendingShowForm(null);
    },
    [pendingShowForm, addToolResult]
  );

  /** Resolves show_form as dismissed so the agent can continue gracefully. */
  const handleShowFormDismiss = useCallback(() => {
    if (!pendingShowForm) return;
    addToolResult({
      tool: "show_form",
      toolCallId: pendingShowForm.toolCallId,
      output: { status: "dismissed" },
    });
    setPendingShowForm(null);
  }, [pendingShowForm, addToolResult]);

  /**
   * Tool call side panel auto-expand and user-close tracking.
   * Opens the panel when new non-interactive tool calls complete; respects manual close.
   */
  const { isChatToolVisible, onToolPanelOpen, onToolPanelClose } = useToolPanel({
    messages,
    initialMessages,
    status: status,
    isCompact,
  });

  /**
   * Handle navigation after the first streaming response completes for a new chat.
   * The client-generated UUID is used as the DB primary key, so we can navigate
   * immediately without waiting for a server-assigned ID.
   */
  useEffect(() => {
    if (isNewChat && status === "ready" && messages.length > 0) {
      /*
        Coding owns its own URL and must not be navigated away from.

        This is the same wrong inference `isCodingSurface` exists to exclude
        (see its declaration): the fallback below reads "no agent and no team"
        as "the Super Agent", which is true on every assistant surface and false
        for coding. A coding session lives at /app/:appId/coding/:sessionId and
        has no agent, so the first completed turn threw the user out of the
        session and onto the Super Agent's chat — mid-conversation, with the
        sandbox still attached to a page no longer on screen.
      */
      if (!isBubbleMode && !isCodingSurface) {
        if (onNewChat) {
          onNewChat(id);
          // The page-level `onNewChat` replaceState's into the URL's agent —
          // which is the supervisor on the New Task page. When the user
          // picked a specialist, we override the URL to that sub-agent's
          // path. Use `history.replaceState` (not RR `navigate`) so the
          // page stays mounted and we don't flash the agent-chat page's
          // skeleton on the hand-off (#981/#983).
          if (effectiveAgentId && effectiveAgentId !== agentId && !teamId) {
            window.history.replaceState(
              null,
              "",
              `/app/${routeAppId}/agents/${effectiveAgentId}/chat/${id}`
            );
          }
        } else {
          const navigationPath = teamId
            ? `/teams/${teamId}/chat/${id}`
            : `/app/${routeAppId}/agents/${effectiveAgentId ?? SUPER_AGENT_ID}/chat/${id}`;
          navigate(navigationPath, { replace: true });
        }
      }
      chatEvents.emit("new-chat", id, { agentId: effectiveAgentId, teamId });
      setIsNewChat(false);
    }
  }, [isNewChat, status, messages.length, id, agentId, effectiveAgentId, teamId, chatEvents, onNewChat, navigate, isBubbleMode, isCodingSurface, routeAppId]);

  /**
   * Hand-off route sync: in an existing chat, when a sub-agent has taken over
   * (active agent != URL agent) and a stream JUST finished, reroute the URL
   * so refresh/share-link lands on that sub-agent. Gated on the
   * streaming→ready transition so the URL doesn't jump the moment the user
   * picks an agent — only after the sub-agent has actually answered.
   *
   * Uses `history.replaceState` (not RR `navigate`) so the parent chat page
   * stays mounted — a full RR navigation re-evaluates the route, triggers
   * the page's `useEffect` to refetch the agent record, and flashes a
   * skeleton loader on every hand-off. Silently swapping the URL keeps the
   * conversation alive in place; chat.tsx's own state (`activeAgent`) is
   * the source of truth for chip / body / persistence.
   */
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const wasStreaming =
      prevStatusRef.current === "streaming" || prevStatusRef.current === "submitted";
    const justFinished = wasStreaming && status === "ready";

    /**
     * Safety net for a stopped artifact stream. When the user stops mid-
     * generation, the AI SDK aborts the connection, so the server's terminal
     * `data-artifact-finish` / `data-artifact-error` event never reaches the
     * client — the canvas would stay stuck on "Generating…" until a reload.
     * When the chat stream ends (ready or error) with an artifact still marked
     * `streaming`, finalize it here so the canvas transitions to Saved/editable
     * live, matching the partial body the server persisted on stop.
     */
    if (
      wasStreaming &&
      (status === "ready" || status === "error") &&
      useArtifactStore.getState().artifact.status === "streaming"
    ) {
      useArtifactStore.getState().finishStream();
    }

    if (
      justFinished &&
      !isNewChat &&
      activeAgent &&
      activeAgent.id !== agentId &&
      !isBubbleMode &&
      !teamId
    ) {
      window.history.replaceState(
        null,
        "",
        `/app/${routeAppId}/agents/${activeAgent.id}/chat/${id}`
      );
    }
    prevStatusRef.current = status;
  }, [status, activeAgent, agentId, isNewChat, id, routeAppId, isBubbleMode, teamId]);

  /**
   * Custom regenerate function that handles regenerating any assistant message in the conversation.
   * Unlike the default Vercel AI SDK regenerate which only regenerates the last message,
   * this allows regenerating any assistant message by:
   * 1. Finding the message to regenerate and its preceding user message
   * 2. Removing only the messages AFTER the one being regenerated (to maintain conversation structure)
   * 3. Setting flags for the server to know which message to update
   * 4. Calling the original regenerate to generate a new response that will update the existing message
   *
   * @param options - Optional options object with messageId. If not provided, regenerates the last message.
   */
  const regenerate = useCallback(async (options?: { messageId?: string }) => {
    const messageId = options?.messageId;

    if (messageId) {
      // Find the index of the message to regenerate
      const messageIndex = messages.findIndex(msg => msg.id === messageId);

      if (messageIndex === -1) {
        console.error('Message not found:', messageId);
        return;
      }

      // Store the message ID for updating on the server
      regeneratingMessageIdRef.current = messageId;

      // Remove all messages AFTER the one being regenerated (keep the message being regenerated)
      // This preserves the conversation structure and the message will be updated with new content
      const newMessages = messages.slice(0, messageIndex + 1);
      setMessages(newMessages);
    }

    // Set the regenerating flag
    isRegeneratingRef.current = true;

    // Notify sidebar that this chat is now streaming
    chatEvents.emit("stream-start", id, { agentId, teamId });

    // Call the original regenerate function
    // This will resend the last user message in the conversation
    await originalRegenerate();
  }, [messages, setMessages, originalRegenerate, chatEvents, id, agentId, teamId]);

  /**
   * Loading status. We treat it as a boolean indicating whether the chat is currently loading.
   * Computed properly based on the chat status.
   */
  const loading = useMemo(() => {
    return hasActiveChatRequest || status === "submitted" || status === "streaming";
  }, [hasActiveChatRequest, status]);

  /**
   * The step list is on screen only while the agent is working.
   *
   * This chat is the only component that knows whether a turn is in flight, so it
   * publishes that fact and every plan surface reads it (`useLivePlan`). Covers a
   * stopped stream and an errored one too — both leave `loading` false, so the
   * checklist stands down rather than freezing mid-step forever.
   *
   * Only the visibility is scoped to the run: the plan itself stays in the store,
   * because the server carries it into the next turn, so "continue" picks the
   * same list back up instead of flashing an empty card.
   */
  useEffect(() => {
    usePlanStore.getState().setRunning(loading);
  }, [loading]);
  useEffect(() => {
    // Leaving the chat ends the run as far as any plan surface is concerned.
    return () => usePlanStore.getState().setRunning(false);
  }, []);

  /**
   * The composer tray, resolved against the live turn status.
   *
   * A host that passes a function gets told whether a turn is in flight, which
   * is knowledge only this component has. The Coding surface needs it: its bar
   * offers a Stop control that must disappear the moment a turn starts, and the
   * session state it otherwise reads is a poll — up to eight seconds — behind.
   * Hosts that pass a plain node are unaffected.
   */
  const composerFooterNode = useMemo(
    () =>
      typeof composerFooter === "function"
        ? composerFooter({ streaming: loading })
        : composerFooter,
    [composerFooter, loading],
  );

  /**
   * Live voice currently starts a separate session that is not yet wired to
   * continue an existing transcript. Keep it available only before the first
   * chat message exists.
   */
  const canStartLiveVoice = voiceLiveEnabled && isNewChat && messages.length === 0 && !initialMessage;

  /**
   * Send chat message to the server. To support multi model input
   * add the attachment list as well to the original chat message.
   *
   * @param message String content of the message
   * @param attachments Array of file attachments
   * @returns Promise resolving to the response from the server
   */
  async function submitChat(
    message: string,
    attachments?: Attachment[],
    options?: ChatSubmitOptions,
  ) {
    // Check if message is provided
    // Skip submission if no message content is available
    if (!message || message.trim() === "") {
      return;
    }

    // Hard block while a `/compact` is running. The composer is already
    // locked in the UI; this guards the non-composer submit paths (queued
    // messages, quick actions) so a turn can never race the summary write.
    if (isCompacting) {
      toast.info("Hang on — compacting the conversation first.");
      return;
    }

    // Record whether this turn originated from a follow-up chip — used to
    // suppress the next turn's chips (the anti chip-spiral rule).
    if (isSuperAgentChat) {
      setLastTurnFromChip(chipFilledPromptRef.current !== null);
      chipFilledPromptRef.current = null;
    }

    // Main Assistant (Super-Agent) chat needs *some* model source: an app-
    // level override, a workspace Super-Agent model, or an active specialist
    // (which brings its own). Without any of those, the server has nothing
    // to dispatch to. Coding is excluded by `isMainAssistantChat`: its model is
    // fixed when the sandbox is provisioned, so there is nothing for the user
    // to choose and this guard would refuse every message.
    if (
      isMainAssistantChat &&
      !activeAgent &&
      !selectedModel &&
      !hasWorkspaceModel
    ) {
      toast.error("Please select a model before sending a message");
      return;
    }

    if (shouldQueueMessage()) {
      queueMessage(message, attachments, options);
      setInput("");
      setQuickActionInput("");
      return;
    }

    // Clear the input immediately for better UX
    setInput("");
    setQuickActionInput("");

    // Include selected agents and appId in the body. `effectiveAgent` lets an
    // active sub-agent (picked via slash/card inside a Super-Agent chat)
    // override the URL agent so the server treats this turn as a direct
    // sub-agent chat rather than a supervisor dispatch.
    const body: ChatRequestBody = effectiveAgent
      ? { agent: effectiveAgent }
      : { ...(agents.length > 0 && { selectedAgents: agents }) };

    // Tell the server this turn is coming from the Super-Agent — either the
    // landing page or a chat already routed via the supervisor — so it can
    // stamp `chat.metadata.cameFromSuperAgent` and persistent the hand-off
    // chip across reloads.
    if (isSuperAgentRouted) {
      body.fromSuperAgent = true;
    }

    if (teamId) body.teamId = teamId;
    if (routeAppId) body.appId = routeAppId;
    if (selectedModel) body.modelOverride = selectedModel;
    if (reasoningEffort) body.reasoningEffort = reasoningEffort;
    if (options?.hidden) body.hidden = true;
    if (options?.steered) body.steered = true;
    /**
     * The step list is deliberately NOT cleared here.
     *
     * Clearing on send looks right — "a new request starts a fresh plan" — but
     * the client cannot tell a new request from a continuation, and the model
     * can. Saying "continue" is not a new task; wiping the list on that message
     * threw away state the model still believed in and nothing restored it, so
     * the panel vanished for the rest of the run. The server now decides:
     * at its next checkpoint the model resubmits the list, replacing it outright
     * if the request really was new (`core/plan/plan-checkpoint.ts`).
     */
    // Notify sidebar that this chat is now streaming
    chatEvents.emit("stream-start", id, { agentId, teamId });
    hasActiveChatRequestRef.current = true;
    setHasActiveChatRequest(true);
    // From here on this tab drives its own turns, so `attachToLiveTurn` must
    // never also open the reconnect stream — see `shouldResume`.
    hasSentFromThisTabRef.current = true;

    // Use the sendMessage method for adding user messages
    await sendMessage(
      {
        role: "user",
        parts: [
          ...(attachments || []).map((file) => ({
            type: "file" as const,
            url: file.url,
            name: file.name,
            mediaType: file.contentType,
          })),
          {
            type: "text",
            text: message,
          },
        ],
        // Trigger messages (like the post-approval "Approved. Execute the
        // plan now." prompt) carry the hidden flag so the renderer can skip
        // them — the plan card's "Approved" badge already conveys the state.
        ...(options?.hidden
          ? { metadata: { createdAt: new Date().toISOString(), hidden: true } }
          : {}),
      },
      {
        body,
      }
    );
  }

  const shouldShowAskQuestionCard = Boolean(pendingAskQuestion);
  const shouldShowComposioConnectCard = Boolean(pendingComposioConnect);
  const shouldShowFormCard = Boolean(pendingShowForm && activeShowFormView);

  const {
    isStoppingStream,
    queuedMessages,
    queueMessage,
    removeQueuedMessage,
    shouldQueueMessage,
    steerQueuedMessage,
    stopStreaming,
  } = useChatMessageQueue({
    loading,
    activeRequestRef: hasActiveChatRequestRef,
    stop,
    setMessages,
    submitChat,
    setDataStatus,
  });

  /**
   * Allow pressing Escape to stop the streaming response.
   * Matches the "Press esc to interrupt" hint shown in the thinking indicator.
   */
  useEffect(() => {
    if (!loading) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        stopStreaming();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [loading, stopStreaming]);

  /**
   * `submitChat` is recreated on every render, so callbacks that must keep a
   * stable `useCallback` identity cannot list it as a dependency without
   * flipping identity on every keystroke — and reading it from the closure
   * freezes them on the first render's `submitChat`, whose `sendMessage` goes
   * stale after the first stream. This ref bridges the two.
   */
  const submitChatRef = useRef(submitChat);
  useEffect(() => {
    submitChatRef.current = submitChat;
  });

  /**
   * Register submitChat with the interactive context (no-op when no provider).
   * This lets tool renderers deep inside Messages call sendMessage() via context.
   */
  const { registerSendMessage } = useInteractiveChat();
  useEffect(() => {
    registerSendMessage((text: string) => submitChat(text));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerSendMessage]);

  /**
   * Auto-send initial message on mount (e.g. after interactive form submission).
   * Uses a ref guard to prevent double-send in React StrictMode.
   * Waits for agentData to load when agentId is set, so the server receives the
   * full agent record and creates the chat with the correct agentId.
   */
  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (!initialMessage || initialMessageSentRef.current) return;
    // Wait for agentData to be loaded when an agentId is provided
    if (agentId && !agentData) return;
    initialMessageSentRef.current = true;
    /**
     * Pass any attachments handed off from the upstream landing page so
     * the file part lands in the first message. We then clear the local
     * attachments state — they're now embedded in the user message and
     * the composer pills should disappear (mirrors what the manual send
     * path does in MultimodalInput).
     */
    submitChat(initialMessage, initialAttachments);
    if (initialAttachments && initialAttachments.length > 0) {
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, agentData]);

  /**
   * Navigates to a fresh chat session for the current context (agent, team, or default).
   * Used by the "New chat" header button.
   *
   * For agent/team chats we navigate straight to the entry surface
   * (`/agents/:agentId` or `/teams/:teamId`) — the detail page IS the
   * new-chat composer. We must NOT route through `/agents/:agentId/chat`,
   * which is the bare `RedirectToAgentDetail` route kept only for stale
   * bookmarks. That route is not wrapped in `AppLayout`, so bouncing through
   * it unmounts and remounts the whole app shell (sidebar, providers, app
   * context) — a full-page-reload flash that only happened on agent chats.
   * The default (SuperAgent) path already targets an `AppLayout`-wrapped
   * route, which is why the main chat was unaffected.
   */
  const handleNewChatClick = useCallback(() => {
    const path = agentId
      ? `/app/${routeAppId}/agents/${agentId}`
      : teamId
        ? `/app/${routeAppId}/teams/${teamId}`
        : `/app/${routeAppId}/chat`;
    navigate(path);
  }, [agentId, teamId, routeAppId, navigate]);

  /**
   * Post-delete redirect: lands on the parent surface (agent landing page,
   * team landing page, or app root) rather than starting a brand-new chat
   * session, so the user isn't dropped into an empty composer they didn't
   * ask for.
   */
  const handlePostDelete = useCallback(() => {
    const path = agentId
      ? `/app/${routeAppId}/agents/${agentId}`
      : teamId
        ? `/app/${routeAppId}/teams/${teamId}`
        : `/app/${routeAppId}`;
    navigate(path);
  }, [agentId, teamId, routeAppId, navigate]);

  /**
   * Persists an inline chat title rename for team chats.
   * Other chat modes keep the header read-only until a backend route exists.
   */
  const handleTitleSave = useCallback(async (nextTitle: string) => {
    const trimmedTitle = nextTitle.trim();

    if (!trimmedTitle || trimmedTitle === currentTitle) {
      return;
    }

    try {
      setIsSavingTitle(true);
      if (teamId) {
        await teamChatService.updateTeamChatTitle(teamId, id, trimmedTitle);
      } else {
        await chatService.updateChatTitle(id, trimmedTitle);
      }
      setCurrentTitle(trimmedTitle);
      chatEvents.emit("update-title", id, { title: trimmedTitle, agentId, teamId });
      onTitleUpdated?.(trimmedTitle);
    } catch {
      toast.error("Failed to update chat title");
      throw new Error("Failed to update chat title");
    } finally {
      setIsSavingTitle(false);
    }
  }, [agentId, chatEvents, currentTitle, id, onTitleUpdated, teamId]);

  const canRenameTitle =
    !isReadonly
    && !isPreview
    && !isBubbleMode
    && !isNewChat
    && !loading;

  /**
   * Copies the agent's published URL to the clipboard.
   * Shows an error toast if the agent has not been published yet.
   */
  const handleCopyAgentUrl = useCallback(async () => {
    if (!agentData?.publishedId) {
      toast.error("Agent is not published yet");
      return;
    }
    const url = `${window.location.origin}/agent/${agentData.publishedId}`;
    await navigator.clipboard.writeText(url).then(
      () => toast.success("Published URL copied"),
      () => toast.error("Failed to copy URL"),
    );
  }, [agentData]);

  /**
   * × on the chip → clear the hand-off. For an in-session pick (activeAgent
   * set but the chat row is still under the supervisor) we just drop the
   * local selection. For a persisted hand-off (the chat is super-agent-routed
   * and the URL is currently a specialist), we also tell the server to flip
   * the chat row back to the supervisor and navigate to that URL so the
   * next turn — and any reload — routes through SuperAgent again.
   */
  const handleClearHandoff = useCallback(async () => {
    // Persisted hand-off case: the chat is routed to a real specialist
    // right now (either via the URL agent or via an in-session `activeAgent`
    // override after the New Task landing's silent replaceState). Flip the
    // server row back to the supervisor, swap the URL silently
    // (history.replaceState avoids the parent page's remount + skeleton
    // flash that a full RR navigate would cause), and flag the local chat
    // state so derived values immediately reflect "we're at the supervisor
    // now". `effectiveAgentId` covers both cases because it folds the
    // URL agent and the in-session pick into one truth — checking
    // `!isSuperAgentRoute` alone misses the New Task path where the URL
    // prop is still null.
    const handingOff =
      !!effectiveAgentId && effectiveAgentId !== SUPER_AGENT_ID;

    // The server flip only applies to a chat that's actually been persisted
    // and re-keyed to a specialist. A brand-new chat (`isNewChat` — no stream
    // has run yet) has no DB row, so calling clear-handoff would 404 with
    // "Chat not found". An in-session pick on such a chat hasn't been
    // re-keyed server-side either, so just drop the local selection below.
    if (cameFromSuperAgent && id && handingOff && !isNewChat) {
      try {
        await chatService.clearChatHandoff(id);
        if (!isBubbleMode && !teamId) {
          window.history.replaceState(
            null,
            "",
            `/app/${routeAppId}/agents/${SUPER_AGENT_ID}/chat/${id}`
          );
        }
        setActiveAgent(null);
        // Drop the lingering selection too — otherwise the next turn would
        // still send `selectedAgents: [Aria]` and the server's
        // single-selection fallback (`effectiveAgentRecord = selectedAgents[0]`)
        // would route to Aria again and re-key the chat right back to her.
        setAgents([]);
        setClearedToSuperAgent(true);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to clear hand-off"
        );
      }
      return;
    }

    // In-session pick (slash/card) — or a brand-new chat that was never
    // persisted — where the chat row hasn't been re-keyed yet. No server
    // call needed, just drop the local selection.
    setActiveAgent(null);
    setAgents([]);
  }, [cameFromSuperAgent, id, effectiveAgentId, isNewChat, isBubbleMode, teamId, routeAppId, setAgents]);

  /**
   * Unified hand-off setter — used by both the slash agent picker (via
   * MultimodalInput's onActiveAgentChange) and the specialist cards. Marks
   * the chat as Super-Agent-routed so the chip and slash picker survive
   * reload (next stream stamps `chat.metadata.cameFromSuperAgent`). Passing
   * `null` (e.g. the × on the chip) routes through `handleClearHandoff` so a
   * persisted hand-off is also reverted server-side.
   */
  const handleActiveAgentChange = useCallback((next: Agent | null) => {
    if (next === null) {
      void handleClearHandoff();
      return;
    }
    setActiveAgent(next);
    setCameFromSuperAgent(true);
    // Picking a specialist re-delegates the conversation, undoing any prior
    // × clear in this session.
    setClearedToSuperAgent(false);
  }, [handleClearHandoff]);

  const handleSpecialistSelect = useCallback((specialist: Agent | null) => {
    handleActiveAgentChange(specialist);
    if (!specialist) {
      setAgents([]);
      return;
    }
    setAgents([specialist]);
  }, [setAgents, handleActiveAgentChange]);

  /**
   * User submitted the AskQuestionCard. Resolve the suspended tool call
   * via the AI SDK so the agent resumes streaming with the answer in
   * hand. The output shape matches the `ask_user_question` tool's
   * `AskUserQuestionAnswer` contract.
   */
  const handleAskQuestionSubmit = useCallback(
    (toolCallId: string, answers: AskUserAnswers, notes?: AskUserNotes) => {
      addToolResult({
        tool: "ask_user_question",
        toolCallId,
        output: notes && Object.keys(notes).length ? { answers, notes } : { answers },
      });
      setPendingAskQuestion(null);
    },
    [addToolResult],
  );

  /**
   * User dismissed the card (Esc / "Dismiss"). Return `declined: true`
   * so the agent can fall back to its best guess and keep going instead
   * of hanging forever waiting for input.
   */
  const handleAskQuestionDismiss = useCallback(
    (toolCallId: string) => {
      addToolResult({
        tool: "ask_user_question",
        toolCallId,
        output: { declined: true },
      });
      setPendingAskQuestion(null);
    },
    [addToolResult],
  );

  /**
   * User finished connecting an integration in the ComposioConnectCard.
   * Resolve the suspended `connect_composio_integration` tool so the agent
   * resumes with the connection in hand.
   */
  const handleComposioConnected = useCallback(
    (toolCallId: string, result: ComposioConnectResult) => {
      addToolResult({
        tool: "connect_composio_integration",
        toolCallId,
        output: result,
      });
      setPendingComposioConnect(null);
    },
    [addToolResult],
  );

  /**
   * User skipped/closed the connect card — return `declined: true` so the
   * agent records it as missing setup and proceeds without the integration.
   */
  const handleComposioConnectDismiss = useCallback(
    (toolCallId: string) => {
      addToolResult({
        tool: "connect_composio_integration",
        toolCallId,
        output: { declined: true },
      });
      setPendingComposioConnect(null);
    },
    [addToolResult],
  );

  // Empty state: show input directly
  // Skip empty state when initialMessage is provided — auto-send will fire
  // immediately, so showing the centered layout would cause a visible flicker
  // as it switches to the bottom-anchored chat layout.
  if (messages.length === 0 && !initialMessage) {
    // Standard empty state — avatar + heading + chat input.
    //
    // Phone browsers get a keyboard-safe variant: the composer is pulled out of
    // the centered hero and pinned to the bottom of the visual-viewport-height
    // column, so it lands just above the soft keyboard in EVERY mobile browser
    // (Chrome / Safari / Edge / Firefox / Opera / Brave). The old centered
    // layout let iOS reveal the focused input by scrolling the nearest scroll
    // container to the top, which dumped the composer under the URL bar with a
    // dead gap above the keyboard. Embeds (`isCompact`) keep the centered
    // layout — they aren't full-page and don't drive the soft keyboard.
    const useMobileEmptyLayout = isMobile && !isCompact;

    // Input Field — replaced by the inline approval card while a plan is
    // drafting or pending, or by the ask-user question card while a
    // clarification tool call is awaiting an answer. Built once and placed
    // either inline with the hero (desktop) or pinned to the bottom (phones).
    const emptyComposer = (
      <div className="w-full">
        {/* CoWork (desktop): folder switcher + controls above the empty-state input.
            No Context toggle here — the panel only appears once work has started. */}
        <CoworkComposerBar showContext={false} />
        {shouldShowFormCard && pendingShowForm && activeShowFormView ? (
          <ShowFormCard
            pending={pendingShowForm}
            view={activeShowFormView}
            onSubmit={handleShowFormSubmit}
            onDismiss={handleShowFormDismiss}
            disabled={loading}
          />
        ) : shouldShowAskQuestionCard && pendingAskQuestion ? (
          <AskQuestionCard
            pending={pendingAskQuestion}
            onSubmit={handleAskQuestionSubmit}
            onDismiss={handleAskQuestionDismiss}
            disabled={loading}
          />
        ) : shouldShowComposioConnectCard && pendingComposioConnect ? (
          <ComposioConnectCard
            pending={pendingComposioConnect}
            onConnected={handleComposioConnected}
            onDismiss={handleComposioConnectDismiss}
            disabled={loading}
          />
        ) : (
          <MultimodalInput
            chatId={id}
            onCompact={handleCompact}
            isCompacting={isCompacting}
            value={input}
            input={quickActionInput || input}
            setInput={setInput}
            isLoading={loading}
            attachmentEnabled={attachmentsAllowed}
            showEmbedBranding={isEmbedSurface && (embedAppearance?.showBranding ?? true)}
            attachments={attachments}
            setAttachments={setAttachments}
            onSubmit={submitChat}
            selectedAgents={agents}
            onToolPanelOpen={onToolPanelOpen}
            onAgentsChange={setAgents}
            activeAgent={chipAgent}
            onActiveAgentChange={handleActiveAgentChange}
            allowAgentPicker={isSuperAgentRouted}
            appId={routeAppId}
            isCompact={isCompact}
            slashMenuPlacement={useMobileEmptyLayout ? "up" : "down"}
            selectedModel={selectedModel}
            onModelChange={handleModelChange}
            reasoningOptions={reasoningOptions}
            reasoningMode={reasoningMode}
            reasoningLoading={reasoningLoading}
            reasoningEffort={reasoningEffort}
            onReasoningEffortChange={setReasoningEffort}
            folderAttachment={isBrowserWorkspace ? folderAttachment : undefined}
            quickActions={agentId ? <FormQuickAction agentId={agentId} onSubmit={submitChat} disabled={false} /> : undefined}
            queuedMessages={queuedMessages}
            onRemoveQueuedMessage={removeQueuedMessage}
            onSteerQueuedMessage={steerQueuedMessage}
            voiceLiveEnabled={canStartLiveVoice}
            onLive={canStartLiveVoice ? onLive : undefined}
            composerFooter={composerFooterNode}
          />
        )}
      </div>
    );

    const emptyHero = (
      <ChatEmptyState
        agentData={agentData}
        teamInfo={!agentId && teamId ? teamInfo : null}
        isCompact={isCompact}
        onSuggestionClick={submitChat}
        specialistAgents={specialistAgents}
        specialistsLoading={specialistsLoading}
        activeSpecialistId={agents[0]?.id ?? null}
        onSpecialistSelect={handleSpecialistSelect}
      >
        {/* Phones render the composer at the bottom (below); everywhere else it
            sits inline between the subtitle and the suggestions. */}
        {useMobileEmptyLayout ? null : emptyComposer}
      </ChatEmptyState>
    );

    // Phone layout: hero scrolls in a flex-1 region; the composer is anchored
    // to the bottom of the `--app-vh` column so it rides just above the soft
    // keyboard without the browser having to scroll it into view.
    if (useMobileEmptyLayout) {
      return (
        <div className="relative flex h-full w-full min-h-0 flex-1 flex-col overflow-hidden bg-background">
          <div className="pointer-events-none absolute inset-0 bg-grid-pattern bg-grid-fade text-slate-400/[0.08]" />
          {/* Bubble embed: header (brand + close) is present on every screen,
              including the empty state (spec 075 — Phase 2). */}
          {isBubbleMode && (
            <div className="relative">
              <ChatBubbleHeader
                agentId={agentId}
                fallbackTitle={agentData?.name}
                agentAvatar={agentData?.avatar}
                showAvatar={false}
                showLanguageSwitcher
                onClose={notifyParentClose}
              />
            </div>
          )}
          {isBrowserWorkspace && (
            <div className="relative w-full">
              <BrowserWorkspaceBanner attachment={folderAttachment} />
            </div>
          )}
          <div className="scrollbar-hide relative flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-[max(0.75rem,env(safe-area-inset-left))] py-4">
            <div className="relative mx-auto my-auto flex w-full max-w-full flex-col items-center">
              {emptyHero}
              {agentId && (
                <FormQuickAction agentId={agentId} onSubmit={submitChat} variant="suggestion" />
              )}
            </div>
          </div>
          <div
            className="relative mx-auto w-full max-w-full shrink-0 px-[max(0.75rem,env(safe-area-inset-left))]"
            style={{ paddingBottom: "max(var(--composer-pb, 0px), env(safe-area-inset-bottom))" }}
          >
            {emptyComposer}
          </div>
        </div>
      );
    }

    // Desktop / tablet / embed: centered hero with the composer inline.
    return (
      <>
        {/* Bubble embed: header (brand + close) is present on every screen,
            including the empty state (spec 075 — Phase 2). */}
        {isBubbleMode && (
          <ChatBubbleHeader
            agentId={agentId}
            fallbackTitle={agentData?.name}
            agentAvatar={agentData?.avatar}
            showAvatar={false}
            showLanguageSwitcher
            onClose={notifyParentClose}
          />
        )}
        {/*
          Scroll-safe vertical centering. `justify-center` is NOT scroll-safe:
          when a shrinking viewport (`--app-vh`) drops below the empty-state
          content height, centered overflow is clipped at BOTH ends with no way
          to scroll to it. `overflow-y-auto` + `my-auto` on the inner block
          centers when there's room and becomes scrollable otherwise.
        */}
        <div className="scrollbar-hide relative flex h-full w-full min-h-0 flex-1 flex-col items-center overflow-y-auto py-4 bg-background">
          <div className="pointer-events-none absolute inset-0 bg-grid-pattern bg-grid-fade text-slate-400/[0.08]" />
          {/**
           * Browser-workspace banner — surfaces "attach a folder",
           * "reconnect", or "browser unsupported" before the user types
           * their first message, so they aren't left guessing why the
           * agent can't see any files.
           */}
          {isBrowserWorkspace && (
            <div className="relative w-full">
              <BrowserWorkspaceBanner attachment={folderAttachment} />
            </div>
          )}
          {/* Empty state content */}
          <div className={cn("relative mx-auto my-auto w-full flex flex-col items-center", isCompact ? "max-w-full px-2" : "max-w-full pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] lg:max-w-3xl xl:max-w-4xl lg:px-4")}>
            {emptyHero}
            {/* Form quick action — rendered as a suggestion row below the sample prompts */}
            {agentId && !isCompact && (
              <FormQuickAction agentId={agentId} onSubmit={submitChat} variant="suggestion" />
            )}
          </div>
        </div>

      </>
    );
  }

  return (
    <div className="relative flex h-full w-full min-h-0 flex-1">
      <motion.div
        ref={chatContainerRef}
        className={cn(
          // `min-w-0` is load-bearing: without it the default flex
          // `min-width: auto` keeps the chat at its content's intrinsic
          // width, so a sibling with `flex-[3]` (the artifact canvas)
          // cannot squeeze this column below ~600px and `isNarrow`
          // never trips. Same reason every other flex child here
          // already pairs `flex-1` with `min-w-0`.
          //
          // No framer-motion `layout` prop here on purpose: layout
          // animates via `transform` and conflicts with AnimatePresence
          // mounting the artifact sibling, which left the chat stuck
          // with a translated transform off-screen. Flex already
          // animates width changes smoothly enough for this case.
          "relative flex flex-1 min-w-0 flex-col bg-background min-h-0 overflow-hidden",
          className
        )}
      >
        <div className="pointer-events-none absolute inset-0 bg-grid-pattern bg-grid-fade text-slate-400/[0.08]" />
        <div className={cn("relative flex flex-1 h-full w-full min-h-0 min-w-0")}>
          <div className="flex min-w-0 flex-1 flex-col min-h-0">
            {/**Chat header - different content based on bubble mode */}
            {isBubbleMode ? (
              <ChatBubbleHeader
                agentId={agentId}
                fallbackTitle={agentData?.name}
                agentAvatar={agentData?.avatar}
                onClose={notifyParentClose}
              />
            ) : title ? (
              <header className="flex shrink-0 items-center justify-between gap-2 overflow-hidden px-3 py-2 lg:p-3">
                {/** The chat title - hidden in preview mode */}
                {!isPreview ? (
                  <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                    {agentData && !hideAgentChip && (
                      <button
                        type="button"
                        onClick={() => navigate(`/app/${routeAppId}/agents/${agentId}`)}
                        className="flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        title={`Open ${agentData.name}`}
                      >
                        <AgentIcon
                          avatar={agentData.avatar}
                          name={agentData.name}
                          size="xs"
                          rounded="md"
                        />
                        <span className="max-w-[160px] truncate font-medium">
                          {agentData.name}
                        </span>
                        <span className="text-muted-foreground/60">·</span>
                      </button>
                    )}
                    <ChatTitle
                      ref={chatTitleRef}
                      title={currentTitle}
                      icon={currentIcon}
                      teamName={!agentId && teamId ? teamInfo?.name : null}
                      isEditable={canRenameTitle}
                      isSaving={isSavingTitle}
                      onSaveTitle={handleTitleSave}
                    />
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  {/**
                   * Secondary header controls — hidden when the chat is rendered
                   * in a narrow column (artifact open, sidebar embed, mobile
                   * drawer, etc). Detected via measured width, so any future
                   * host that shrinks the chat gets the same condensed header
                   * without telling this component anything. Share / Edit /
                   * Delete remain accessible through the More dropdown below.
                   */}
                  {!isNarrow && (
                    <>
                      {/** Surface-specific controls (e.g. the Coding diff toggle) */}
                      {headerActions}

                      {/**
                       * New chat button.
                       *
                       * Not on the Coding surface: a coding session is bound to
                       * a repository and a sandbox, so "new chat" has nothing to
                       * create — starting fresh means New Task in the rail,
                       * which asks for the repo first.
                       */}
                      {!isReadonly && !isCodingSurface && (
                        <button
                          onClick={handleNewChatClick}
                          className="text-sm font-medium text-foreground border border-border rounded-lg px-3 py-1 bg-muted/40 hover:bg-muted transition-colors cursor-pointer"
                          title={teamId ? "New Topic" : "New chat"}
                        >
                          {teamId ? "New Topic" : "New chat"}
                        </button>
                      )}

                      {/**
                       * The total costs consumed by the chat.
                       *
                       * Hidden on Coding: the model runs inside the sandbox
                       * through its own credential, so this badge has no turn
                       * usage to add up and sat at $0.00 forever.
                       */}
                      {!isCodingSurface && (
                        <ChatCreditsBadge
                          messages={messages}
                          chatId={id}
                          teamId={teamId}
                          status={status}
                          initialMessagesCount={initialMessages.length}
                        />
                      )}

                      {/**
                       * Sandbox-files browser — only when the agent has a
                       * server-side workspace. Browser-workspace agents
                       * surface their folder picker through the prompt-toolbar
                       * FolderSelector chip instead, since their files live
                       * on the user's machine, not on the server.
                       *
                       * Coding has its own workspace — the cloned repository —
                       * and reads it through the Changes panel, not this
                       * browser, which points at the agent workspace instead.
                       */}
                      {isWorkspaceEnabled && !isBrowserWorkspace && !coworkDeviceId && !isCodingSurface && (
                        <button
                          onClick={() => setIsFileBrowserOpen(true)}
                          className="p-1 hover:bg-muted rounded transition-colors cursor-pointer"
                          title="Browse workspace files"
                        >
                          {isFileBrowserOpen
                            ? <FolderOpen className="size-4 text-primary transition-colors" />
                            : <FolderClosed className="size-4 text-muted-foreground hover:text-foreground transition-colors" />
                          }
                        </button>
                      )}

                      {/** Debug Explorer — dev/admin only (also ⌘⇧D, ?debug=1) */}
                      {debugAllowed && (
                        <button
                          onClick={() => toggleDebug()}
                          className={cn(
                            "p-1 rounded transition-colors cursor-pointer hover:bg-muted",
                            isDebugOpen && "bg-muted",
                          )}
                          title="Debug explorer (⌘⇧D)"
                        >
                          <Bug
                            className={cn(
                              "size-4 transition-colors",
                              isDebugOpen ? "text-primary" : "text-muted-foreground hover:text-foreground",
                            )}
                          />
                        </button>
                      )}

                      {/** Delete button - hidden in preview mode */}
                      {!isReadonly && !isPreview && (
                        <DeleteChatDialog chatId={id} onDeleted={handlePostDelete}>
                          <button
                            className="p-1 hover:bg-destructive/10 rounded transition-colors cursor-pointer"
                            title="Delete chat"
                          >
                            <Trash2 className="size-4 text-destructive hover:text-destructive/90 transition-colors" />
                          </button>
                        </DeleteChatDialog>
                      )}
                    </>
                  )}


                  {/** More button — hidden in preview, and in CoWork when the
                      only item would be Share (a local-folder task isn't shareable). */}
                  {!isReadonly && !isPreview &&
                    ((isNarrow && isWorkspaceEnabled && !isBrowserWorkspace && !coworkDeviceId) ||
                      (isNarrow && debugAllowed) ||
                      !coworkDeviceId ||
                      (showAdminActions && agentId)) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="p-1 hover:bg-muted rounded transition-colors cursor-pointer"
                          title="More options"
                        >
                          <MoreHorizontal className="size-4 text-muted-foreground hover:text-foreground transition-colors" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        {/* Browse files — the header folder button is hidden in
                            the condensed (narrow/mobile) header, so surface the
                            workspace file browser here instead. */}
                        {isNarrow && isWorkspaceEnabled && !isBrowserWorkspace && !coworkDeviceId && (
                          <DropdownMenuItem
                            onClick={() => setIsFileBrowserOpen(true)}
                            className="cursor-pointer"
                          >
                            <FolderClosed className="h-4 w-4 mr-2" />
                            Browse files
                          </DropdownMenuItem>
                        )}

                        {isNarrow && debugAllowed && (
                          <DropdownMenuItem
                            onClick={() => toggleDebug()}
                            className="cursor-pointer"
                          >
                            <Bug className="h-4 w-4 mr-2" />
                            Debug explorer
                          </DropdownMenuItem>
                        )}

                        {/* Share is omitted in CoWork — a task bound to a local
                            folder can't be shared to another user. */}
                        {!isReadonly && !coworkDeviceId && (
                          <DropdownMenuItem
                            onClick={() => setIsShareDialogOpen(true)}
                            className="cursor-pointer"
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                        )}

                        {showAdminActions && agentId && (
                          <>
                            {/* Separator only when these actions follow Share —
                                otherwise it renders as a stray empty strip under
                                the last item (e.g. Super Agent chats, which have
                                no editable agent). */}
                            {!isReadonly && !coworkDeviceId && <DropdownMenuSeparator />}
                            <DropdownMenuItem
                              onClick={() => navigate(`/app/${routeAppId}/agents/${agentId}/edit`)}
                              className="cursor-pointer"
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit agent
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={handleCopyAgentUrl}
                              className="cursor-pointer"
                            >
                              <Link2 className="h-4 w-4 mr-2" />
                              Copy URL
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </header>
            ) : null}

            {/**
             * Browser-workspace banner above the conversation — visible
             * mid-chat when the folder gets detached / permission expires
             * mid-session, so the user can recover without leaving the chat.
             */}
            {isBrowserWorkspace && (
              <BrowserWorkspaceBanner attachment={folderAttachment} />
            )}

            {/**
         * In case the chat id is not passed or the chat id is not found then
         * don't render the chat messages.
         */}
            <div className="flex min-w-0 flex-1 overflow-hidden w-full">
              <Conversation className="relative flex min-w-0 flex-1 overflow-hidden w-full">
                <ConversationContent className="!gap-0 pt-4 px-0 pb-[calc(5rem+env(safe-area-inset-bottom))]">
                  <Messages
                    status={status}
                    surface={surface}
                    messages={messages}
                    sandboxId={sandboxId}
                    isReadonly={isReadonly}
                    chatId={id as string}
                    setMessages={setMessages}
                    regenerate={regenerate}
                    isCompact={isCompact}
                    textToSpeechEnabled={agentData?.agentSettings?.textToSpeech ?? false}
                    voiceSettings={agentData?.agentSettings?.voiceSettings}
                    agentStreams={agentStreams}
                    agentStreamStats={agentStreamStats}
                    agentFinalTexts={agentFinalTexts}
                    compactions={compactions}
                  />
                  {(loading || (initialMessage && messages.length === 0)) && (
                    <ThinkingMessage message={isStoppingStream ? "Stopping response..." : dataStatus} />
                  )}
                  {error && !isCreditError(error) && !isAbortError(error) && (
                    <div className="mx-auto w-full max-w-full lg:max-w-3xl xl:max-w-4xl px-3 lg:px-4">
                      <div className="flex items-start gap-2 px-3 py-2">
                        <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" />
                        <p className="text-sm text-destructive break-words">{error.message}</p>
                      </div>
                    </div>
                  )}
                  {!isReadonly && (
                    <FollowupSuggestions
                      chatId={id}
                      status={status}
                      enabled={
                        isSuperAgentChat ||
                        !!agentData?.agentSettings?.showFollowUp
                      }
                      lastTurnFromChip={lastTurnFromChip}
                      onSelect={sendChipPrompt}
                    />
                  )}
                  {/* Spacer for scroll padding */}
                  <div className="min-w-[24px] shrink-0" />
                </ConversationContent>
                <ConversationScrollButton />
              </Conversation>
            </div>


            {/**
         * The multimodal input will be used to get the user input. This will be used to
         * get the user input from the chat window. The input will be used to send the message
         * to the server. Replaced by the plan approval card while a plan is pending.
         */}
            {!isReadonly && (
              <div
                className={cn(
                  "relative mx-auto w-full",
                  isCompact
                    ? "max-w-full px-2"
                    : isNarrow
                      ? "max-w-full pb-3 pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))]"
                      : "max-w-3xl xl:max-w-4xl px-3 lg:px-4"
                )}
                style={{
                  paddingBottom:
                    "max(var(--composer-pb, 0px), env(safe-area-inset-bottom))",
                }}
              >
                {/* CoWork (desktop): approval + folder/autonomy bar, above the composer. */}
                <CoworkApprovalCard />
                <CoworkComposerBar />
                {/**
                 * The agent's live step list. Renders only when there is a plan,
                 * and stands down inside CoWork, where the Context panel already
                 * shows it. This is what makes the checklist universal — normal
                 * chat, Super Agent, single agents, every workspace type.
                 */}
                <PlanChecklistCard
                  suppressed={!!coworkDeviceId && !isArtifactVisible && !isLocalFileVisible}
                />
        {shouldShowFormCard && pendingShowForm && activeShowFormView ? (
                  <ShowFormCard
                    pending={pendingShowForm}
                    view={activeShowFormView}
                    onSubmit={handleShowFormSubmit}
                    onDismiss={handleShowFormDismiss}
                    disabled={loading}
                  />
                ) : shouldShowAskQuestionCard && pendingAskQuestion ? (
                  <AskQuestionCard
                    pending={pendingAskQuestion}
                    onSubmit={handleAskQuestionSubmit}
                    onDismiss={handleAskQuestionDismiss}
                    disabled={loading}
                  />
                ) : shouldShowComposioConnectCard && pendingComposioConnect ? (
                  <ComposioConnectCard
                    pending={pendingComposioConnect}
                    onConnected={handleComposioConnected}
                    onDismiss={handleComposioConnectDismiss}
                    disabled={loading}
                  />
                ) : (
                  <MultimodalInput
                    chatId={id}
                    onCompact={handleCompact}
                    isCompacting={isCompacting}
                    input={quickActionInput || input}
                    setInput={setInput}
                    isLoading={loading}
                    attachmentEnabled={attachmentsAllowed}
            showEmbedBranding={isEmbedSurface && (embedAppearance?.showBranding ?? true)}
                    onSubmit={submitChat}
                    value={input}
                    status={status}
                    stop={stopStreaming}
                    attachments={attachments}
                    onToolPanelOpen={onToolPanelOpen}
                    setAttachments={setAttachments}
                    messages={messages}
                    selectedAgents={agents}
                    onAgentsChange={setAgents}
                    activeAgent={chipAgent}
                    onActiveAgentChange={handleActiveAgentChange}
                    allowAgentPicker={isSuperAgentRouted}
                    appId={routeAppId}
                    className={cn(isCompact ? "max-w-full" : "max-w-3xl xl:max-w-4xl")}
                    isCompact={isCompact}
                    // Phones: drop the "AI may be inaccurate" disclaimer once a
                    // conversation is underway — it only needs to be seen once,
                    // on the empty state, and it wastes scarce vertical room
                    // above the keyboard. Desktop keeps it (web unchanged).
                    showHint={!isMobile}
                    selectedModel={selectedModel}
                    onModelChange={handleModelChange}
                    reasoningOptions={reasoningOptions}
                    reasoningMode={reasoningMode}
                    reasoningLoading={reasoningLoading}
                    reasoningEffort={reasoningEffort}
                    onReasoningEffortChange={setReasoningEffort}
                    folderAttachment={isBrowserWorkspace ? folderAttachment : undefined}
                    quickActions={agentId ? <FormQuickAction agentId={agentId} onSubmit={submitChat} disabled={false} /> : undefined}
                    queuedMessages={queuedMessages}
                    onRemoveQueuedMessage={removeQueuedMessage}
                    onSteerQueuedMessage={steerQueuedMessage}
                    voiceLiveEnabled={canStartLiveVoice}
                    onLive={canStartLiveVoice ? onLive : undefined}
                    composerFooter={composerFooterNode}
                  />
                )}
              </div>
            )}
          </div>
          {/* File-agent tree mounts as a top-level sibling below the
              artifact AnimatePresence so it sits at the right edge of the
              window without nesting inside the chat motion.div (which
              caused asymmetric centering for the messages). */}
        </div>
      </motion.div>

      {/* Animated spacer to push chat when panel opens */}
      {canShowToolSidepanel && !isReadonly && !isCompact && (
        <motion.div
          className="shrink-0"
          animate={{
            width: isChatToolVisible ? 680 : 0,
          }}
          transition={{
            duration: 0.25,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        />
      )}


      {/**
       * Show the tool calling result canvas. This will be expanded when the user
       * clicks on the tool call result in the chat messages.
       * Hidden in readonly mode (shared chats) and compact mode (widget).
       * Visibility also controlled by AI Settings > Supervisor Agent > Computer Preview toggle.
       * Gated by VITE_ENABLE_TOOL_SIDEPANEL env var (default: disabled) and by
       * the workspace tool-output visibility setting (issue #1332).
       */}
      {canShowToolSidepanel && !isReadonly && !isCompact && (
        <ToolCallSidePanel
          chatId={id}
          currentIndex={0}
          agentName="Agent"
          messages={messages}
          isOpen={isChatToolVisible}
          showComputerPreview={aiSettings.supervisor.capabilities.showComputerPreview}
          onClose={onToolPanelClose}
          agentStatus={status}
        />
      )}

      {/**
       * Chat Debug Explorer — dev/admin-only ground-truth timeline panel.
       * Mounted next to the tool side panel so it shares the chat layout flow.
       */}
      {debugAllowed && (
        <DebugExplorerPanel
          chatId={id}
          isOpen={isDebugOpen}
          onClose={() => toggleDebug(false)}
        />
      )}

      {/**
       * Share dialog for creating and managing public share links
       */}
      <ShareDialog
        open={isShareDialogOpen}
        onOpenChange={setIsShareDialogOpen}
        chatId={id}
      />

      {/**
       * Workspace file browser — a single controlled instance (no inline
       * trigger) so it can be opened from either the header folder button
       * (desktop) or the overflow menu's "Browse files" (narrow/mobile, where
       * the folder button is hidden).
       */}
      {isWorkspaceEnabled && !isBrowserWorkspace && !coworkDeviceId && (
        <FileBrowser
          chatId={id as string}
          agentStatus={status}
          open={isFileBrowserOpen}
          onOpenChange={setIsFileBrowserOpen}
        />
      )}

      {/**
       * Artifact canvas — renders as a sibling pane on the right when the
       * artifact store is visible. The chat motion.div above shrinks to
       * 400px so the existing Messages + MultimodalInput stay live on the
       * left without any duplication. AnimatePresence drives mount/unmount
       * animation; framer-motion `layout` on the chat column animates the
       * width transition in sync.
       */}
      <AnimatePresence>{isArtifactVisible && <Artifact />}</AnimatePresence>

      {/**
       * Local-file viewer — opens when the user double-clicks a file in
       * the file-agent tree. Suppressed while the agent's artifact canvas
       * is open so the two side panes never compete for the same row.
       */}
      <AnimatePresence>
        {!isArtifactVisible && isLocalFileVisible && <LocalFileViewer />}
      </AnimatePresence>

      {/**
       * File-agent tree — anchored to the right edge as a top-level sibling
       * of the chat. Hidden when either viewer (artifact or local-file) is
       * open so the viewer can take the full pane on the right; closing the
       * viewer brings it back so the user can pick another file.
       */}
      {isBrowserWorkspace
        && folderAttachment.handle
        && !folderAttachment.needsReconnect
        && !isCompact
        && !isArtifactVisible
        && !isLocalFileVisible && (
        <aside className="z-[1] flex w-[340px] shrink-0 flex-col border-l border-border/60 bg-background">
          <FileTreePanel attachment={folderAttachment} className="h-full" />
        </aside>
      )}

      {/**
       * CoWork Context panel (desktop) — the right pane in CoWork mode: working
       * folder, working files, and commands with undo. Yields to the artifact
       * canvas / local-file viewer so the right panes never compete.
       */}
      {/* CoWork (desktop): restore + auto-reconnect this chat's folder/activity. */}
      <CoworkChatSync chatId={id as string} />
      {coworkDeviceId && !isArtifactVisible && !isLocalFileVisible && <CoworkContextPanel />}
    </div>
  );
}
